import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import withStyles from '@mui/styles/withStyles';

import IconButton from '@mui/material/IconButton';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import Paper from '@mui/material/Paper';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import Grid from '@mui/material/Grid';

import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';

import TextField from '@mui/material/TextField';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/AddCircle';
import DuplicateIcon from '@mui/icons-material/FileCopy';
import DelaiReferenceIcon from '@mui/icons-material/AccessAlarm';
import DelaiExtensionIcon from '@mui/icons-material/AddAlarm';
import MenuIcon from '@mui/icons-material/Menu';

import green from '@mui/material/colors/green';

import Fichier from 'components/Shared/Fichier';
import DropZone from 'components/Shared/DropZone';
import ArticleGestionImportArea from 'components/Shared/ArticleGestionImportArea';
import FamilleImportArea from 'components/Shared/FamilleImportArea';
import SousFamilleImportArea from 'components/Shared/SousFamilleImportArea';
import withLoader from 'components/Shared/withLoader';

import FournisseurEmailSuiviSAVByFoussier from './FournisseurEmailSuiviSAVByFoussier';
import FournisseurPreuveAchatObligatoire from './FournisseurPreuveAchatObligatoire';
import FournisseurCodeSiegeEtiquette from './FournisseurCodeSiegeEtiquette';

import { GarantieType } from 'constants.js';
import { GarantieTypeNoms } from 'constants.js';
import { PerimetresSAV } from 'constants.js';

import { updateDropZone } from 'store/common/commonActions';

import localStorageHelper from 'utils/localStorageHelper';

const ContainerWithLoader = withLoader(Paper);

const styles = theme => ({

    card: {
        height: '100%',
        border: theme.palette.primary.main + ' 1px solid',
        transition: 'border-color 1s',
        '&:hover': {
            border: theme.palette.secondary[500] + ' 1px solid',
        }
    },

    newCardContent: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column'
    },

    conditionCardHeader: {
        paddingBottom: 0
    },

    conditionCardContent: {
        paddingTop: 0
    },

    displayConditionTextField: {
        marginBottom: theme.spacing(2),
    },

    conditionsAccordionDetailsRoot: {
        flexDirection: 'column'
    },

    buttonProgress: {
        color: green[500],
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12
    }
});

/**
 * @return Un composant html fieldset pour l'affichage des paramètres des conditions
 * @param {string} legend - La légende du fieldset
 * @param {string} content - Du contenu, on fait un split sur les nouvelles lignes
 * @param {string} children - composants enfants
 */
function Fieldset({ legend, content, children, style }) {

    let lines = content ? content.split('\n') : [];

    return (
        <fieldset style={{ ...style, borderRadius: '8px', paddingBottom: '0px', backgroundColor: 'rgb(250, 250, 250)' }}>
            <legend style={{ fontWeight: 'lighter', fontSize: 'small' }}>{legend}</legend>
            {lines.map((line, index) => {
                return (
                    <p key={index} style={{ marginTop: '0px', marginBottom: '4px', fontSize: '0.75em' }}>{line}</p>
                );
            })}
            {children && (
                <div style={{ paddingBottom: '4px' }}>
                    {children}
                </div>
            )}
        </fieldset>
    );
}

/**
 * @return Un composant hmtl fieldset pour l'affichage des paramètres des conditions
 * @param {number} garantieType - Le type de garantie de la condition, on affiche rien si HorsGarantie
 * @param {number} delaiReference
 * @param {number} delaiExtensionGarantie - pas affiché si === 0
 */
function DelaiSubHeader({ garantieType, delaiReference, delaiExtensionGarantie }) {

    if (garantieType === GarantieType.HorsGarantie) {
        return (<span style={{ minHeight: '32px' }}>&nbsp;</span>);
    }
    return (
        <div style={{ display: 'flex', alignItems: 'end' }}>
            <DelaiReferenceIcon /><div style={{ marginRight: '8px' }}>{delaiReference}</div>
            {delaiExtensionGarantie && (<React.Fragment><DelaiExtensionIcon /><div>{delaiExtensionGarantie}</div></React.Fragment>)}
        </div>
    );
}

function ScrollHere({ id, children }) {

    useEffect(() => {

        setTimeout(() => {
            let element = document.getElementById(id);
            window.scrollTo({ top: element.offsetParent.offsetTop, left: 0, behavior: 'smooth' });
        }, 200);

    }, [id]);

    return (<div id={id}>{children}</div>);
}

function DuplicateButton({ detailId, onClick, sourceType, displayArticle }) {

    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton color="primary" aria-label="Dupliquer" title="Dupliquer" onClick={handleClick}>
                <DuplicateIcon />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                <MenuItem onClick={() => { handleClose(); onClick(detailId, sourceType, ConditionType.Generale); }}>Dupliquer comme condition générale</MenuItem>
                <MenuItem onClick={() => { handleClose(); onClick(detailId, sourceType, ConditionType.Famille); }}>Dupliquer comme condition pour les familles</MenuItem>
                <MenuItem onClick={() => { handleClose(); onClick(detailId, sourceType, ConditionType.SousFamille); }}>Dupliquer comme condition pour les sous-familles</MenuItem>

                {displayArticle && (
                    <MenuItem onClick={() => { handleClose(); onClick(detailId, sourceType, ConditionType.Article); }}>Dupliquer comme condition pour les articles</MenuItem>
                )}

            </Menu>
        </>
    );
}

/**
 * Fonction de tri des conditions
 */
const sortConditionFunction = (a, b) => {

    if (a.libelle !== b.libelle) {
        return a.libelle > b.libelle;
    }

    if (a.perimetre === PerimetresSAV.Societe && b.perimetre !== PerimetresSAV.Societe) {
        return -1;
    }
    if (a.perimetre !== PerimetresSAV.Societe && b.perimetre === PerimetresSAV.Societe) {
        return 1;
    }
    if (a.perimetre === b.perimetre) {
        if (a.garantieType === b.garantieType) {
            return 0;
        }
        return a.garantieType > b.garantieType;
    }

    return a.perimetre > b.perimetre;
}

// Fonction pour filtrer par identifiant unique
function uniqueIdFilterFunction(value, index, self) {
    return (
        self
            .slice(index + 1)
            .findIndex(
                (currentValue, i, r) => currentValue.id === value.id
            ) === -1
    );
}

// Fonction pour filtrer par valeur unique
function uniqueReferenceFilterFunction(value, index, self) {
    return (
        self
            .slice(index + 1)
            .findIndex(
                (currentValue, i, r) => currentValue.reference === value.reference
            ) === -1
    );
}

// Fonction pour filtrer par code unique
function uniqueCodeFilterFunction(value, index, self) {
    return (
        self
            .slice(index + 1)
            .findIndex(
                (currentValue, i, r) => currentValue.code === value.code
            ) === -1
    );
}

/**
 * Nouvelle adresse par défaut
 */
const newAdresse = {
    "libelle": '',
    "adresse": ''
};

/**
 * Nouveau responsable  par défaut
 */
const newResponsable = {
    "contact": "",
    "telephone": "",
    "email": ""
};

/**
 * Nouveau standard téléphonique par défaut
 */
const newStandardTelephonique = {
    "numero": "",
    "horaires": ""
};

const ConditionType = {
    Undefined: -1,
    Generale: 0,
    Article: 1,
    Famille: 2,
    SousFamille: 3,
};

/**
 * Nouvelle condition par défaut
 */
const newCondition = {
    type: ConditionType.Undefined,
    "garantieType": '',
    "perimetre": PerimetresSAV.Societe,
    "articles": [],
    "nouveauxArticles": [],

    "familles": [],
    "nouvellesFamilles": [],

    "sousFamilles": [],
    "nouvellesSousFamilles": [],

    "libelle": '',
    "detail": {
        "delaiReference": 365,
        "delaiExtensionGarantie": '',
        "pieceJoindreColis": '',
        "perimetreApplicationSAV": '',
        "preconisationFacturationClient": "",
        "reglePriseEnChargePort": '',
        "engagementDelaiResolution": '',
        "commentaire": '',
        "mentionsParticulieres": '',
        "ficheRetourFournisseurId": '',
        "nouvelleFicheRetourFournisseurId": '',
        "adresseId": '',
        "responsableId": '',
        "standardTelephoniqueId": ''
    }
};

function FournisseurGestionConditionsSAVEditeur({ classes, fournisseurId, updateDropZone }) {

    const [loading, setLoading] = useState(true);

    const [expandedAccordion, setExpandedAccordion] = useState('NoExpandedAccordion');

    const [fournisseur, setFournisseur] = useState({
        emailSuiviSAVByFoussier: '',
        codeSiege: '',
        adresses: [],
        responsables: [],
        standardTelephoniques: [],
        conditionsSAV: [],
        conditionsSAVArticle: [],
        conditionsSAVFamille: [],
        conditionsSAVSousFamille: []
    });

    const [editionNouvelleAdresse, setEditionNouvelleAdresse] = useState(false);
    const [nouvelleAdresse, setNouvelleAdresse] = useState(null);
    const [editedAdresseId, setEditedAdresseId] = useState(0);

    const [editionNouveauResponsable, setEditionNouveauResponsable] = useState(false);
    const [nouveauResponsable, setNouveauResponsable] = useState(null);
    const [editedResponsableId, setEditedResponsableId] = useState(0);

    const [editionNouveauStandardTelephonique, setEditionNouveauStandardTelephonique] = useState(false);
    const [nouveauStandardTelephonique, setNouveauStandardTelephonique] = useState(null);
    const [editedStandardTelephoniqueId, setEditedStandardTelephoniqueId] = useState(0);

    const [editionNouvelleCondition, setEditionNouvelleCondition] = useState(false);
    const [editionNouvelleConditionArticle, setEditionNouvelleConditionArticle] = useState(false);
    const [editionNouvelleConditionFamille, setEditionNouvelleConditionFamille] = useState(false);
    const [editionNouvelleConditionSousFamille, setEditionNouvelleConditionSousFamille] = useState(false);
    const [nouvelleCondition, setNouvelleCondition] = useState(null);

    const [editedConditionDetailId, setEditedConditionDetailId] = useState(0);

    const [disabledNouvelleCondition, setDisabledNouvelleCondition] = useState({ errors: [] });

    const [fichiers, setFichiers] = useState([]);

    const [isAdmin] = useState(localStorageHelper.isAdmin());

    /* Fiches.. */
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [fichierId, setFichierId] = useState(0);
    const [applyingFile, setApplyingFile] = useState(false);
    const [applyingFileErr, setApplyingFileErr] = useState(false);
    /* ...Fiches */

    // Fetch des infos concernant le fournisseur au chargement de la page
    useEffect(() => {
        const fetchData = async () => {

            await axios("/api/fournisseurs/administration/" + fournisseurId + "/conditionsSAV")
                .then(function (response) {

                    let fournisseurData = response.data;

                    if (!fournisseurData.emailSuiviSAVByFoussier) {
                        fournisseurData.emailSuiviSAVByFoussier = '';
                    }

                    if (!fournisseurData.codeSiege) {
                        fournisseurData.codeSiege = '';
                    }

                    fournisseurData.conditionsSAV = fournisseurData.conditionsSAV.sort(sortConditionFunction);
                    fournisseurData.conditionsSAVArticle = fournisseurData.conditionsSAVArticle.sort(sortConditionFunction);

                    fournisseurData.conditionsSAVFamille = fournisseurData.conditionsSAVFamille.sort(sortConditionFunction);
                    fournisseurData.conditionsSAVSousFamille = fournisseurData.conditionsSAVSousFamille.sort(sortConditionFunction);

                    setFournisseur(fournisseurData);

                })
                .catch(function (error) {
                    console.log(error);
                })
                .then(function () {
                    // always executed
                    setLoading(false);
                });

        };


        if (loading) {
            fetchData();
        }

    }, [fournisseurId, loading]);
    // https://www.robinwieruch.de/react-hooks-fetch-data


    // Vérification de la validité d'une nouvelle condition
    useEffect(() => {

        //console.log("Vérification de la validité d'une nouvelle condition", nouvelleCondition);

        let result = {
            errors: []
        };

        if (!nouvelleCondition) {
            result.errors.push({ key: "nouvelleCondition", message: "Pas de condition de définie" });

            setDisabledNouvelleCondition(result);
            return;
        }

        if (nouvelleCondition.garantieType === '') {
            result.errors.push({ key: "garantieType", message: "Veuillez sélectionner un type de garantie" });
        }

        // On garde le contrôle mais l'erreur ne doit pas pouvoir se produire car on ne peut plus modifier le périmètre
        if (!nouvelleCondition.perimetre) {
            result.errors.push({ key: "perimetre", message: "Veuillez entrer un périmètre" });
        }

        if ((editionNouvelleConditionArticle || editionNouvelleConditionFamille || editionNouvelleConditionSousFamille) && !nouvelleCondition.libelle) {
            result.errors.push({ key: "libelle", message: "Veuillez entrer un libellé" });
        }

        // Vérification de doublon à l'ajout
        if (editionNouvelleCondition) {
            let condition = fournisseur.conditionsSAV.find(x => x.garantieType === nouvelleCondition.garantieType && x.perimetre === nouvelleCondition.perimetre);
            if (condition) {
                result.errors.push({ key: "unicite", message: "Garantie déjà définie pour ce type de garantie sur le périmètre" });
            }
        }

        // TODO ici il faudra vérifier qu'un article ne soit pas dupliqué, ainsi que le libellé
        if (editionNouvelleConditionArticle) {
            //let condition = fournisseur.conditionsSAVArticle.find(x => x.garantieType === nouvelleCondition.garantieType && x.perimetre === nouvelleCondition.perimetre && x.reference === nouvelleCondition.reference);
            //if (condition) {
            //    result.errors.push({ key: "unicite", message: "Garantie déjà définie pour ce type de garantie sur le périmètre pour cette référence" });
            //}
        }


        if (!nouvelleCondition.detail.adresseId) {
            result.errors.push({ key: "adresseId", message: "Veuillez sélectionner une adresse" });
        }
        if (!nouvelleCondition.detail.pieceJoindreColis) {
            result.errors.push({ key: "pieceJoindreColis", message: "Veuillez entrer une ou des pièces à joindre dans le colis." });
        }
        if (!nouvelleCondition.detail.reglePriseEnChargePort) {
            result.errors.push({ key: "reglePriseEnChargePort", message: "Veuillez entrer une règle de prise en charge du port." });
        }

        // Le fichier n'est plus obligatoire
        //if (!nouvelleCondition.detail.ficheRetourFournisseur && !nouvelleCondition.detail.nouvelleFicheRetourFournisseurId && !nouvelleCondition.detail.ficheRetourFournisseurId) {
        //    result.errors.push({ key: "ficheRetourFournisseurId", message: "Veuillez sélectionner une fiche retour fournisseur" });
        //}


        setDisabledNouvelleCondition(result);
        return;
    },
        [nouvelleCondition, editionNouvelleCondition, editionNouvelleConditionArticle, editionNouvelleConditionFamille, editionNouvelleConditionSousFamille, fournisseur.conditionsSAV, fournisseur.conditionsSAVArticle]);


    // Construction d'une liste des fichiers du fournisseur
    useEffect(() => {

        let files = fournisseur.conditionsSAV.map(x => x.detail.ficheRetourFournisseur);
        files = files.concat(fournisseur.conditionsSAVFamille.map(x => x.detail.ficheRetourFournisseur));
        files = files.concat(fournisseur.conditionsSAVSousFamille.map(x => x.detail.ficheRetourFournisseur));
        files = files.concat(fournisseur.conditionsSAVArticle.map(x => x.detail.ficheRetourFournisseur));

        files = files.filter(file => !!file);

        files = files
            .filter(uniqueIdFilterFunction)
            .sort((a, b) => a.nom > b.nom);

        setFichiers(files);

    },
        [fournisseur.conditionsSAV, fournisseur.conditionsSAVFamille, fournisseur.conditionsSAVSousFamille, fournisseur.conditionsSAVArticle]
    );


    // Gestion du menu d'une fiche
    const handleClickMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };


    /**
     * Post d'une condition via l'api, sans utilisation du store
     */
    const postAdresse = async (adresse) => {

        await axios.post("/api/fournisseurs/" + fournisseurId + "/conditionsSAV/adresses", adresse)
            .then(data => {

                setFournisseur(
                    {
                        ...fournisseur,
                        adresses: [...fournisseur.adresses, data.data]
                    });

                handleClickCancelNouvelleAdresse();
                return data.data;
            })
            .catch(err => {
                console.log(err);
                return null;
            });
    };

    const patchAdresse = async (adresseId, param) => {

        await axios.patch("/api/fournisseurs/conditionsSAV/adresses/" + adresseId, param)
            .then(data => {

                console.log(data);

                setFournisseur(
                    {
                        ...fournisseur,
                        adresses: fournisseur.adresses.map(x => x.id !== adresseId ? { ...x } : { ...x, libelle: param.libelle, adresse: param.adresse })
                    });

                handleClickCancelAdresse();
                return data.data;
            })
            .catch(err => {
                console.log(err);
                return null;
            });
    };

    const deleteAdresse = async (adresseId) => {

        await axios.delete("/api/fournisseurs/conditionsSAV/adresses/" + adresseId)
            .then(data => {

                console.log(data);

                setFournisseur(
                    {
                        ...fournisseur,
                        adresses: fournisseur.adresses.filter(x => x.id !== adresseId)
                    });

                return true;
            })
            .catch(err => {
                console.log(err);
                return false;
            });
    };

    const postResponsable = async (responsable) => {

        await axios.post("/api/fournisseurs/" + fournisseurId + "/conditionsSAV/responsables", responsable)
            .then(data => {

                setFournisseur(
                    {
                        ...fournisseur,
                        responsables: [...fournisseur.responsables, data.data]
                    });

                handleClickCancelNouveauResponsable();
                return data.data;
            })
            .catch(err => {
                console.log(err);
                return null;
            });
    };

    const patchResponsable = async (responsableId, param) => {

        await axios.patch("/api/fournisseurs/conditionsSAV/responsables/" + responsableId, param)
            .then(data => {

                console.log(data);

                setFournisseur(
                    {
                        ...fournisseur,
                        responsables: fournisseur.responsables.map(x => x.id !== responsableId ? { ...x } : { ...x, contact: param.contact, telephone: param.telephone, email: param.email })
                    });

                handleClickCancelResponsable();
                return data.data;
            })
            .catch(err => {
                console.log(err);
                return null;
            });
    };

    const deleteResponsable = async (responsableId) => {

        await axios.delete("/api/fournisseurs/conditionsSAV/responsables/" + responsableId)
            .then(data => {

                console.log(data);

                setFournisseur(
                    {
                        ...fournisseur,
                        responsables: fournisseur.responsables.filter(x => x.id !== responsableId)
                    });

                return true;
            })
            .catch(err => {
                console.log(err);
                return false;
            });
    };

    const postStandardTelephonique = async (standardTelephonique) => {

        await axios.post("/api/fournisseurs/" + fournisseurId + "/conditionsSAV/standardTelephoniques", standardTelephonique)
            .then(data => {

                setFournisseur(
                    {
                        ...fournisseur,
                        standardTelephoniques: [...fournisseur.standardTelephoniques, data.data]
                    });

                handleClickCancelNouveauStandardTelephonique();
                return data.data;
            })
            .catch(err => {
                console.log(err);
                return null;
            });
    };

    const patchStandardTelephonique = async (standardTelephoniqueId, param) => {

        await axios.patch("/api/fournisseurs/conditionsSAV/standardTelephoniques/" + standardTelephoniqueId, param)
            .then(data => {

                console.log(data);

                setFournisseur(
                    {
                        ...fournisseur,
                        standardTelephoniques: fournisseur.standardTelephoniques.map(x => x.id !== standardTelephoniqueId ? { ...x } : { ...x, numero: param.numero, horaires: param.horaires })
                    });

                handleClickCancelStandardTelephonique();
                return data.data;
            })
            .catch(err => {
                console.log(err);
                return null;
            });
    };

    const deleteStandardTelephonique = async (standardTelephoniqueId) => {

        await axios.delete("/api/fournisseurs/conditionsSAV/standardTelephoniques/" + standardTelephoniqueId)
            .then(data => {

                console.log(data);

                setFournisseur(
                    {
                        ...fournisseur,
                        standardTelephoniques: fournisseur.standardTelephoniques.filter(x => x.id !== standardTelephoniqueId)
                    });

                return true;
            })
            .catch(err => {
                console.log(err);
                return false;
            });
    };

    const postCondition = async (condition) => {

        console.log("postCondition start", condition);

        //switch (condition.type) {
        //    case ConditionType.Article:
        //        condition.codRefs = condition.nouveauxArticles.map(article => article.reference);
        //        break;
        //    case ConditionType.Famille:
        //        condition.codRefs = condition.nouvellesFamilles.map(famille => famille.code);
        //        break;
        //    case ConditionType.SousFamille:
        //        condition.codRefs = condition.nouvellesSousFamilles.map(sousFamille => sousFamille.code);
        //        break;
        //    case ConditionType.Generale:
        //    default:
        //        break;
        //}

        console.log("postCondition before post", condition);

        await axios.post("/api/fournisseurs/" + fournisseurId + "/conditionsSAV", condition)
            .then(data => {

                switch (condition.type) {
                    case ConditionType.Generale:
                        setFournisseur(
                            {
                                ...fournisseur,
                                conditionsSAV: [...fournisseur.conditionsSAV, data.data].sort(sortConditionFunction)
                            });
                        break;
                    case ConditionType.Article:
                        setFournisseur(
                            {
                                ...fournisseur,
                                conditionsSAVArticle: [...fournisseur.conditionsSAVArticle, data.data].sort(sortConditionFunction)
                            });
                        break;
                    case ConditionType.Famille:
                        setFournisseur(
                            {
                                ...fournisseur,
                                conditionsSAVFamille: [...fournisseur.conditionsSAVFamille, data.data].sort(sortConditionFunction)
                            });
                        break;
                    case ConditionType.SousFamille:
                        setFournisseur(
                            {
                                ...fournisseur,
                                conditionsSAVSousFamille: [...fournisseur.conditionsSAVSousFamille, data.data].sort(sortConditionFunction)
                            });
                        break;
                    default:
                        break;
                }


                handleClickCancelCondition();
                return data.data;
            })
            .catch(err => {
                console.log(err);
                return null;
            });
    };

    const deleteCondition = async (detailId) => {

        await axios.delete("/api/fournisseurs/conditionsSAV/" + detailId)
            .then(data => {

                console.log(data);

                setFournisseur(
                    {
                        ...fournisseur,
                        conditionsSAV: fournisseur.conditionsSAV.filter(x => x.detail.id !== detailId),
                        conditionsSAVArticle: fournisseur.conditionsSAVArticle.filter(x => x.detail.id !== detailId),
                        conditionsSAVFamille: fournisseur.conditionsSAVFamille.filter(x => x.detail.id !== detailId),
                        conditionsSAVSousFamille: fournisseur.conditionsSAVSousFamille.filter(x => x.detail.id !== detailId),
                    });

                return true;
            })
            .catch(err => {
                console.log(err);
                return false;
            });
    };

    const patchCondition = async (condition) => {

        console.log("patchCondition", condition);

        let param = { ...condition.detail };

        param.type = condition.type;
        param.libelle = condition.libelle;
        param.codRefs = condition.codRefs;


        console.log("patchCondition param", param);

        await axios.patch("/api/fournisseurs/conditionsSAV/details/" + condition.detail.id, param)
            .then(data => {


                console.log(data);

                setFournisseur(
                    {
                        ...fournisseur,
                        conditionsSAV: fournisseur.conditionsSAV.map(x => x.detail.id !== condition.detail.id ? { ...x } : { ...data.data }),
                        conditionsSAVArticle: fournisseur.conditionsSAVArticle.map(x => x.detail.id !== condition.detail.id ? { ...x } : { ...data.data }),
                        conditionsSAVFamille: fournisseur.conditionsSAVFamille.map(x => x.detail.id !== condition.detail.id ? { ...x } : { ...data.data }),
                        conditionsSAVSousFamille: fournisseur.conditionsSAVSousFamille.map(x => x.detail.id !== condition.detail.id ? { ...x } : { ...data.data })
                    });

                handleClickCancelCondition();
                return data;
            })
            .catch(err => {
                console.log(err);
                return null;
            });
    };

    // ADRESSES
    const handleClickNouvelleAdresse = () => {
        setEditedAdresseId(0);
        setNouvelleAdresse(newAdresse);
        setEditionNouvelleAdresse(true);
    };

    const handleClickCancelNouvelleAdresse = () => {
        setNouvelleAdresse(newAdresse);
        setEditionNouvelleAdresse(false);
    };

    const handleClickValidNouvelleAdresse = async () => {
        await postAdresse(nouvelleAdresse);
    };

    const handleClickDeleteAdresse = async (adresseId) => {
        await deleteAdresse(adresseId);
    };

    const handleClickEditAdresse = async (adresseId) => {

        const adresse = fournisseur.adresses.find(x => x.id === adresseId);

        setNouvelleAdresse(adresse);
        setEditedAdresseId(adresseId);
        setEditionNouvelleAdresse(false);
    };

    const handleClickCancelAdresse = () => {
        setNouvelleAdresse(newAdresse);
        setEditedAdresseId(0);
    };

    const handleClickValidAdresse = async () => {
        await patchAdresse(editedAdresseId, nouvelleAdresse);
    };


    // RESPONSABLE
    const handleClickNouveauResponsable = () => {
        setEditedResponsableId(0);
        setNouveauResponsable(newResponsable);
        setEditionNouveauResponsable(true);
    };

    const handleClickCancelNouveauResponsable = () => {
        setNouveauResponsable(newResponsable);
        setEditionNouveauResponsable(false);
    };

    const handleClickValidNouveauResponsable = async () => {
        await postResponsable(nouveauResponsable);
    };

    const handleClickDeleteResponsable = async (responsableId) => {
        await deleteResponsable(responsableId);
    };

    const handleClickEditResponsable = async (responsableId) => {

        const responsable = fournisseur.responsables.find(x => x.id === responsableId);

        setNouveauResponsable(responsable);
        setEditedResponsableId(responsableId);
        setEditionNouveauResponsable(false);
    };

    const handleClickCancelResponsable = () => {
        setNouveauResponsable(newResponsable);
        setEditedResponsableId(0);
    };

    const handleClickValidResponsable = async () => {
        await patchResponsable(editedResponsableId, nouveauResponsable);
    };

    // STANDARD TELEPHONIQUE
    const handleClickNouveauStandardTelephonique = () => {
        setEditedStandardTelephoniqueId(0);
        setNouveauStandardTelephonique(newStandardTelephonique);
        setEditionNouveauStandardTelephonique(true);
    };

    const handleClickCancelNouveauStandardTelephonique = () => {
        setNouveauStandardTelephonique(newStandardTelephonique);
        setEditionNouveauStandardTelephonique(false);
    };

    const handleClickValidNouveauStandardTelephonique = async () => {
        await postStandardTelephonique(nouveauStandardTelephonique);
    };

    const handleClickDeleteStandardTelephonique = async (standardTelephoniqueId) => {
        await deleteStandardTelephonique(standardTelephoniqueId);
    };

    const handleClickEditStandardTelephonique = async (standardTelephoniqueId) => {

        const standardTelephonique = fournisseur.standardTelephoniques.find(x => x.id === standardTelephoniqueId);

        setNouveauStandardTelephonique(standardTelephonique);
        setEditedStandardTelephoniqueId(standardTelephoniqueId);
        setEditionNouveauStandardTelephonique(false);
    };

    const handleClickCancelStandardTelephonique = () => {
        setNouveauStandardTelephonique(newStandardTelephonique);
        setEditedStandardTelephoniqueId(0);
    };

    const handleClickValidStandardTelephonique = async () => {
        await patchStandardTelephonique(editedStandardTelephoniqueId, nouveauStandardTelephonique);
    };


    // FICHES
    const handleChangeFichier = (event) => {
        console.log("handleChangeFichier", event.target.name, event.target.value);

        console.log("handleChangeFichier", event.target.fichiers);


        let id = 0;

        if (event.target.value.length > 0) {
            id = event.target.value[0];
        }

        setFichierId(id);

    };

    const handleAppliquerFichier = async ({ id }) => {

        let useId = 0;

        if (id) {
            useId = id;
            setAnchorEl(false);
        }
        else {
            useId = fichierId;
        }

        if (!useId) {
            return;
        }

        setApplyingFile(true);
        setApplyingFileErr(false);

        await axios.post(`/api/fournisseurs/${fournisseurId}/appliquer-fichier/${useId}`)
            .then(data => {


                let newFournisseur = {
                    ...fournisseur,
                    conditionsSAV: fournisseur.conditionsSAV.map(x => {
                        return {
                            ...x, detail: { ...x.detail, ficheRetourFournisseur: { id: useId } }
                        };
                    }),
                    conditionsSAVArticle: fournisseur.conditionsSAVArticle.map(x => {
                        return {
                            ...x, detail: { ...x.detail, ficheRetourFournisseur: { id: useId } }
                        };
                    }),
                    conditionsSAVFamille: fournisseur.conditionsSAVFamille.map(x => {
                        return {
                            ...x, detail: { ...x.detail, ficheRetourFournisseur: { id: useId } }
                        };
                    }),
                    conditionsSAVSousFamille: fournisseur.conditionsSAVSousFamille.map(x => {
                        return {
                            ...x, detail: { ...x.detail, ficheRetourFournisseur: { id: useId } }
                        };
                    })
                };

                setFournisseur(newFournisseur);

                setFichierId(0);
                var dropZone = {
                    fichiers: []
                };
                updateDropZone(dropZone);

                return;
            })
            .catch(err => {
                console.log(err);
                setApplyingFileErr(true);
                return null;
            })
            .finally(() => {

                setApplyingFile(false);
            });


    }

    const handleSupprimerFichier = async (fichierId) => {

        setAnchorEl(false);

        if (!fichierId) {
            return;
        }

        //setApplyingFile(true);
        //setApplyingFileErr(false);

        await axios.post(`/api/fournisseurs/${fournisseurId}/supprimer-fichier/${fichierId}`)
            .then(data => {


                let newFournisseur = {
                    ...fournisseur,
                    conditionsSAV: fournisseur.conditionsSAV.map(x => {
                        return {
                            ...x, detail: {
                                ...x.detail, ficheRetourFournisseur: x.detail.ficheRetourFournisseur && x.detail.ficheRetourFournisseur.id === fichierId ? null : x.detail.ficheRetourFournisseur ? { ...x.detail.ficheRetourFournisseur } : null
                            }
                        };
                    }),
                    conditionsSAVArticle: fournisseur.conditionsSAVArticle.map(x => {
                        return {
                            ...x, detail: { ...x.detail, ficheRetourFournisseur: x.detail.ficheRetourFournisseur && x.detail.ficheRetourFournisseur.id === fichierId ? null : { ...x.detail.ficheRetourFournisseur } }
                        };
                    }),
                    conditionsSAVFamille: fournisseur.conditionsSAVFamille.map(x => {
                        return {
                            ...x, detail: { ...x.detail, ficheRetourFournisseur: x.detail.ficheRetourFournisseur && x.detail.ficheRetourFournisseur.id === fichierId ? null : { ...x.detail.ficheRetourFournisseur } }
                        };
                    }),
                    conditionsSAVSousFamille: fournisseur.conditionsSAVSousFamille.map(x => {
                        return {
                            ...x, detail: { ...x.detail, ficheRetourFournisseur: x.detail.ficheRetourFournisseur && x.detail.ficheRetourFournisseur.id === fichierId ? null : { ...x.detail.ficheRetourFournisseur } }
                        };
                    })
                };

                setFournisseur(newFournisseur);

                setFichierId(0);
                var dropZone = {
                    fichiers: []
                };
                updateDropZone(dropZone);

                return;
            })
            .catch(err => {
                console.log(err);
                //setApplyingFileErr(true);
                return null;
            })
            .finally(() => {

                //setApplyingFile(false);
            });

    }


    // CONDITION
    const handleClickDeleteCondition = async (detailId) => {
        await deleteCondition(detailId);
    };

    const handleClickEditCondition = (detailId, type) => {
        console.log("handleClickEditCondition", detailId);

        let condition = {};

        switch (type) {
            case ConditionType.Generale:
                condition = fournisseur.conditionsSAV.find(x => x.detail.id === detailId);
                break;
            case ConditionType.Article:
                condition = fournisseur.conditionsSAVArticle.find(x => x.detail.id === detailId);
                condition.nouveauxArticles = [...condition.articles];
                break;
            case ConditionType.Famille:
                condition = fournisseur.conditionsSAVFamille.find(x => x.detail.id === detailId);
                condition.nouvellesFamilles = [...condition.familles];
                break;
            case ConditionType.SousFamille:
                condition = fournisseur.conditionsSAVSousFamille.find(x => x.detail.id === detailId);
                condition.nouvellesSousFamilles = [...condition.sousFamilles];
                break;
            default:
                break;
        }

        condition.type = type;



        condition.detail.ficheRetourFournisseurId = condition.detail.ficheRetourFournisseur ? condition.detail.ficheRetourFournisseur.id : '';

        console.log("handleClickEditCondition", condition);

        setNouvelleCondition(condition);
        setEditedConditionDetailId(detailId);


    };

    const handleClickNouvelleConditionGenerale = () => {
        setEditedConditionDetailId(0);

        setEditionNouvelleConditionArticle(false);
        setEditionNouvelleConditionFamille(false);
        setEditionNouvelleConditionSousFamille(false);

        setNouvelleCondition({ ...newCondition, detail: { ...newCondition.detail }, type: ConditionType.Generale });
        setEditionNouvelleCondition(true);
    };

    const handleClickNouvelleConditionArticle = () => {
        setEditedConditionDetailId(0);

        setEditionNouvelleCondition(false);
        setEditionNouvelleConditionFamille(false);
        setEditionNouvelleConditionSousFamille(false);

        setNouvelleCondition({ ...newCondition, detail: { ...newCondition.detail }, type: ConditionType.Article });
        setEditionNouvelleConditionArticle(true);
    };

    const handleClickNouvelleConditionFamille = () => {
        setEditedConditionDetailId(0);

        setEditionNouvelleCondition(false);
        setEditionNouvelleConditionArticle(false);
        setEditionNouvelleConditionSousFamille(false);

        setNouvelleCondition({ ...newCondition, detail: { ...newCondition.detail }, type: ConditionType.Famille });
        setEditionNouvelleConditionFamille(true);
    };

    const handleClickNouvelleConditionSousFamille = () => {
        setEditedConditionDetailId(0);

        setEditionNouvelleCondition(false);
        setEditionNouvelleConditionArticle(false);
        setEditionNouvelleConditionFamille(false);

        setNouvelleCondition({ ...newCondition, detail: { ...newCondition.detail }, type: ConditionType.SousFamille });
        setEditionNouvelleConditionSousFamille(true);
    };

    const handleClickDuplicateCondition = (detailId, sourceType, destinationType) => {
        console.log("handleClickDuplicateCondition", detailId, sourceType, destinationType);

        let conditionExistante = {};


        switch (sourceType) {
            case ConditionType.Generale:
                conditionExistante = fournisseur.conditionsSAV.find(x => x.detail.id === detailId);
                break;
            case ConditionType.Article:
                conditionExistante = fournisseur.conditionsSAVArticle.find(x => x.detail.id === detailId);
                break;
            case ConditionType.Famille:
                conditionExistante = fournisseur.conditionsSAVFamille.find(x => x.detail.id === detailId);
                break;
            case ConditionType.SousFamille:
                conditionExistante = fournisseur.conditionsSAVSousFamille.find(x => x.detail.id === detailId);
                break;
            default:
                break;
        }

        let condition = {
            ...conditionExistante,
            type: destinationType,
            libelle: '',
            articles: [],
            nouveauxArticles: [],

            familles: [],
            nouvellesFamilles: [],

            sousFamilles: [],
            nouvellesSousFamilles: [],

            detail: {
                ...conditionExistante.detail,
                id: 0,
                ficheRetourFournisseurId: conditionExistante.detail.ficheRetourFournisseur ? conditionExistante.detail.ficheRetourFournisseur.id : '',
                nouvelleFicheRetourFournisseurId: ''
            }
        };


        // On annule l'id pour ne pas faire un patch mais un post
        condition.detail.id = 0;

        console.log("handleClickDuplicateCondition", condition);


        setEditedConditionDetailId(0);
        setNouvelleCondition(condition);



        switch (destinationType) {
            case ConditionType.Generale:
                setEditionNouvelleCondition(true);
                setEditionNouvelleConditionArticle(false);
                setEditionNouvelleConditionFamille(false);
                setEditionNouvelleConditionSousFamille(false);
                setExpandedAccordion('conditions');
                break;
            case ConditionType.Article:
                setEditionNouvelleCondition(false);
                setEditionNouvelleConditionArticle(true);
                setEditionNouvelleConditionFamille(false);
                setEditionNouvelleConditionSousFamille(false);
                setExpandedAccordion('conditionsArticle');
                break;
            case ConditionType.Famille:
                setEditionNouvelleCondition(false);
                setEditionNouvelleConditionArticle(false);
                setEditionNouvelleConditionFamille(true);
                setEditionNouvelleConditionSousFamille(false);
                setExpandedAccordion('conditionsFamille');
                break;
            case ConditionType.SousFamille:
                setEditionNouvelleCondition(false);
                setEditionNouvelleConditionArticle(false);
                setEditionNouvelleConditionFamille(false);
                setEditionNouvelleConditionSousFamille(true);
                setExpandedAccordion('conditionsSousFamille');
                break;
            default:
                break;
        }

    };

    const handleClickCancelCondition = () => {
        console.log("handleClickCancelCondition");
        setEditedConditionDetailId(0);
        setEditionNouvelleCondition(false);
        setEditionNouvelleConditionArticle(false);
        setEditionNouvelleConditionFamille(false);
        setEditionNouvelleConditionSousFamille(false);
        setNouvelleCondition(null);
    };

    const handleClickValidCondition = async () => {
        console.log("handleClickValidCondition", nouvelleCondition);


        let condition = {
            ...nouvelleCondition,
            detail: { ...nouvelleCondition.detail }
        };

        if (condition.detail.nouvelleFicheRetourFournisseurId) {
            condition.detail.ficheRetourFournisseurId = condition.detail.nouvelleFicheRetourFournisseurId;
        }
        delete condition.detail.nouvelleFicheRetourFournisseurId;


        switch (condition.type) {
            case ConditionType.Article:
                if (condition.nouveauxArticles !== undefined && condition.nouveauxArticles !== null) {
                    condition.codRefs = condition.nouveauxArticles.map(article => article.reference);
                }
                else {
                    condition.codRefs = [];
                }
                break;
            case ConditionType.Famille:
                if (condition.nouvellesFamilles !== undefined && condition.nouvellesFamilles !== null) {
                    condition.codRefs = condition.nouvellesFamilles.map(famille => famille.code);
                }
                else {
                    condition.codRefs = [];
                }
                break;
            case ConditionType.SousFamille:
                if (condition.nouvellesSousFamilles !== undefined && condition.nouvellesSousFamilles !== null) {
                    condition.codRefs = condition.nouvellesSousFamilles.map(sousFamille => sousFamille.code);
                }
                else {
                    condition.codRefs = [];
                }
                break;
            case ConditionType.Generale:
            default:
                break;
        }

        delete condition.nouveauxArticles;
        delete condition.nouvellesFamilles;
        delete condition.nouvellesSousFamilles;


        if (!condition.detail.id) {
            console.log("handleClickValidCondition nouvelleCondition", condition);
            await postCondition(condition);
            return;
        } else {

            if (condition.detail.id) {
                console.log("handleClickValidCondition modification Condition");

                if (condition.detail.ficheRetourFournisseur) {
                    condition.detail.ficheRetourFournisseurId = condition.detail.ficheRetourFournisseurId ? condition.detail.ficheRetourFournisseurId : condition.detail.ficheRetourFournisseur.Id;
                    delete condition.detail.ficheRetourFournisseur;
                }

                console.log("handleClickValidCondition after delete condition.detail.ficheRetourFournisseur", condition);


                await patchCondition(condition);
            }
            return;
        }

    };

    const handleChangeFieldNouvelleCondition = (name, value, detail) => {
        //console.log("handleChangeFieldNouvelleCondition", name, value, detail);


        let condition = {
            ...nouvelleCondition,
        };

        if (detail) {
            condition.detail[name] = value;
        } else {
            condition[name] = value;
        }


        setNouvelleCondition(condition);

    };

    const handleChangeFichierNouvelleCondition = (event) => {
        //console.log("handleChangeFichierNouvelleCondition", event.target.name, event.target.value);

        let condition = {
            ...nouvelleCondition,
        };

        if (event.target.value.length > 0) {
            condition.detail.nouvelleFicheRetourFournisseurId = event.target.value[0];
        }
        else {
            condition.detail.nouvelleFicheRetourFournisseurId = 0;
        }

        setNouvelleCondition(condition);

    };

    const handleDeleteReferenceNouvelleCondition = (reference) => {

        //console.log("handleDeleteReferenceNouvelleCondition", reference);

        let condition = {
            ...nouvelleCondition,
        };
        condition.nouveauxArticles = condition.nouveauxArticles.filter(x => x.reference !== reference);
        setNouvelleCondition(condition);
    };

    const handleDeleteFamilleNouvelleCondition = (famille) => {

        //console.log("handleDeleteReferenceNouvelleCondition", reference);

        let condition = {
            ...nouvelleCondition,
        };
        condition.nouvellesFamilles = condition.nouvellesFamilles.filter(x => x.code !== famille.code);
        setNouvelleCondition(condition);
    };

    const handleDeleteSousFamilleNouvelleCondition = (sousFamille) => {

        //console.log("handleDeleteReferenceNouvelleCondition", reference);

        let condition = {
            ...nouvelleCondition,
        };
        condition.nouvellesSousFamilles = condition.nouvellesSousFamilles.filter(x => x.code !== sousFamille.code);
        setNouvelleCondition(condition);
    };


    const handleImportArticles = (articlesValides) => {

        console.log("handleImportArticles 1", articlesValides);


        let condition = {
            ...nouvelleCondition,
        };
        condition.nouveauxArticles = condition.nouveauxArticles.concat(articlesValides);
        condition.nouveauxArticles = condition.nouveauxArticles.filter(uniqueReferenceFilterFunction);
        setNouvelleCondition(condition);


        console.log("handleImportArticles 2", condition);
    };

    const handleImportFamilles = (famillesValides) => {

        console.log("handleImportFamilles 1", famillesValides);


        let condition = {
            ...nouvelleCondition,
        };
        condition.nouvellesFamilles = condition.nouvellesFamilles.concat(famillesValides);
        condition.nouvellesFamilles = condition.nouvellesFamilles.filter(uniqueCodeFilterFunction);
        setNouvelleCondition(condition);


        console.log("handleImportFamilles 2", condition);
    };

    const handleImportSousFamilles = (sousFamillesValides) => {

        console.log("handleImportSousFamilles 1", sousFamillesValides);


        let condition = {
            ...nouvelleCondition,
        };
        condition.nouvellesSousFamilles = condition.nouvellesSousFamilles.concat(sousFamillesValides);
        condition.nouvellesSousFamilles = condition.nouvellesSousFamilles.filter(uniqueCodeFilterFunction);
        setNouvelleCondition(condition);


        console.log("handleImportSousFamilles 2", condition);
    };

    return (
        <React.Fragment>

            <ContainerWithLoader isLoading={loading}>

                <Accordion expanded={expandedAccordion === 'emailSuiviSAVByFoussier'} TransitionProps={{ mountOnEnter: true }} onChange={(event, isExpanded) => setExpandedAccordion(isExpanded ? 'emailSuiviSAVByFoussier' : 'NoExpandedAccordion')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">SAV BY FOUSSIER</Typography>
                    </AccordionSummary>
                    <AccordionDetails>

                        <FournisseurEmailSuiviSAVByFoussier
                            fournisseurId={fournisseurId}
                            valeur={fournisseur.emailSuiviSAVByFoussier}
                        />

                        <FournisseurPreuveAchatObligatoire
                            fournisseurId={fournisseurId}
                            valeur={fournisseur.preuveAchatObligatoire}
                        />

                    </AccordionDetails>
                </Accordion>


                <Accordion
                    expanded={expandedAccordion === 'numeroFournisseurEtiquette'}
                    TransitionProps={{ mountOnEnter: true }}
                    onChange={(event, isExpanded) => setExpandedAccordion(isExpanded ? 'numeroFournisseurEtiquette' : 'NoExpandedAccordion')}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Numéro Fournisseur pour Etiquette</Typography>
                    </AccordionSummary>

                    <AccordionDetails>
                        <FournisseurCodeSiegeEtiquette
                            fournisseurId={fournisseurId}
                            valeur={fournisseur.codeSiege}
                        />
                    </AccordionDetails>
                </Accordion>


                <Accordion expanded={expandedAccordion === 'adresses'} TransitionProps={{ mountOnEnter: true }} onChange={(event, isExpanded) => setExpandedAccordion(isExpanded ? 'adresses' : 'NoExpandedAccordion')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Adresses</Typography>
                    </AccordionSummary>
                    <AccordionDetails>

                        <Grid container spacing={3}>
                            <Grid item xs={3}>
                                <Card className={classes.card}>

                                    {!editionNouvelleAdresse && (
                                        <CardContent className={classes.newCardContent}>
                                            <IconButton
                                                color="secondary"
                                                size="medium"
                                                title="Ajouter une adresse"
                                                onClick={handleClickNouvelleAdresse}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                            <Typography gutterBottom variant="body1">
                                                Ajouter une adresse
                                            </Typography>
                                        </CardContent>
                                    )}

                                    {editionNouvelleAdresse && (
                                        <React.Fragment>
                                            <CardContent>
                                                <Typography gutterBottom variant="h6">
                                                    Nouvelle adresse
                                                </Typography>

                                                <TextField
                                                    autoFocus
                                                    color="secondary"
                                                    fullWidth
                                                    variant="filled"
                                                    label="Libellé"
                                                    placeholder=""
                                                    helperText=""
                                                    required
                                                    value={nouvelleAdresse.libelle}
                                                    onChange={e => setNouvelleAdresse({ ...nouvelleAdresse, libelle: e.target.value })}

                                                    size="small"
                                                />
                                                <TextField
                                                    color="secondary"
                                                    fullWidth
                                                    variant="filled"
                                                    label="Adresse"
                                                    multiline
                                                    rows="3"
                                                    required
                                                    value={nouvelleAdresse.adresse}
                                                    onChange={e => setNouvelleAdresse({ ...nouvelleAdresse, adresse: e.target.value })}
                                                />

                                            </CardContent>
                                            <CardActions>
                                                <Button onClick={handleClickCancelNouvelleAdresse}>Annuler</Button>
                                                <Button disabled={!nouvelleAdresse.libelle || !nouvelleAdresse.adresse} onClick={handleClickValidNouvelleAdresse}>Valider</Button>
                                            </CardActions>
                                        </React.Fragment>
                                    )}

                                </Card>
                            </Grid>


                            {fournisseur.adresses.map(adresse => {

                                return (

                                    <Grid item xs={3} key={adresse.id}>

                                        {editedAdresseId !== adresse.id && (
                                            <Card className={classes.card}>
                                                <CardContent>
                                                    <Typography gutterBottom variant="h6">
                                                        {adresse.libelle}
                                                    </Typography>
                                                    {adresse.adresse.split('\n').map((ligne, index) => (
                                                        <Typography gutterBottom variant="body1" key={index}>
                                                            {ligne}
                                                        </Typography>
                                                    ))}
                                                </CardContent>
                                                <CardActions>
                                                    <IconButton color="primary" aria-label="Supprimer" title="Supprimer l'adresse" onClick={() => handleClickDeleteAdresse(adresse.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                    <IconButton color="primary" aria-label="Modifier" title="Modifier l'adresse" onClick={() => handleClickEditAdresse(adresse.id)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </CardActions>
                                            </Card>
                                        )}

                                        {editedAdresseId === adresse.id && (
                                            <Card className={classes.card}>
                                                <CardContent>
                                                    <Typography gutterBottom variant="h6">
                                                        Modification d'adresse
                                                    </Typography>

                                                    <TextField
                                                        autoFocus
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Libellé"
                                                        placeholder=""
                                                        helperText=""
                                                        required
                                                        value={nouvelleAdresse.libelle}
                                                        onChange={e => setNouvelleAdresse({ ...nouvelleAdresse, libelle: e.target.value })}

                                                        size="small"
                                                    />
                                                    <TextField
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Adresse"
                                                        multiline
                                                        rows="3"
                                                        required
                                                        value={nouvelleAdresse.adresse}
                                                        onChange={e => setNouvelleAdresse({ ...nouvelleAdresse, adresse: e.target.value })}
                                                    />

                                                </CardContent>
                                                <CardActions>
                                                    <Button onClick={handleClickCancelAdresse}>Annuler</Button>
                                                    <Button disabled={!nouvelleAdresse.libelle || !nouvelleAdresse.adresse} onClick={handleClickValidAdresse}>Valider</Button>
                                                </CardActions>
                                            </Card>
                                        )}

                                    </Grid>
                                )
                            })}


                        </Grid>

                    </AccordionDetails>
                </Accordion>


                <Accordion expanded={expandedAccordion === 'contacts'} TransitionProps={{ mountOnEnter: true }} onChange={(event, isExpanded) => setExpandedAccordion(isExpanded ? 'contacts' : 'NoExpandedAccordion')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Contacts</Typography>
                    </AccordionSummary>
                    <AccordionDetails>


                        <Grid container spacing={3}>
                            <Grid item xs={3}>
                                <Card className={classes.card}>


                                    {!editionNouveauResponsable && (
                                        <CardContent className={classes.newCardContent}>
                                            <IconButton
                                                color="secondary"
                                                size="medium"
                                                title="Ajouter un responsable"
                                                onClick={handleClickNouveauResponsable}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                            <Typography gutterBottom variant="body1">
                                                Ajouter un contact
                                            </Typography>
                                        </CardContent>
                                    )}

                                    {editionNouveauResponsable && (
                                        <React.Fragment>
                                            <CardContent>
                                                <Typography gutterBottom variant="h6">
                                                    Nouveau contact
                                                </Typography>

                                                <TextField
                                                    autoFocus
                                                    color="secondary"
                                                    fullWidth
                                                    variant="filled"
                                                    label="Contact"
                                                    placeholder=""
                                                    helperText=""
                                                    required
                                                    value={nouveauResponsable.contact}
                                                    onChange={e => setNouveauResponsable({ ...nouveauResponsable, contact: e.target.value })}

                                                    size="small"
                                                />
                                                <TextField
                                                    color="secondary"
                                                    fullWidth
                                                    variant="filled"
                                                    label="Téléphone"
                                                    required
                                                    value={nouveauResponsable.telephone}
                                                    onChange={e => setNouveauResponsable({ ...nouveauResponsable, telephone: e.target.value })}
                                                />
                                                <TextField
                                                    color="secondary"
                                                    fullWidth
                                                    variant="filled"
                                                    label="Email"
                                                    required
                                                    value={nouveauResponsable.email}
                                                    onChange={e => setNouveauResponsable({ ...nouveauResponsable, email: e.target.value })}
                                                />

                                            </CardContent>
                                            <CardActions>
                                                <Button onClick={handleClickCancelNouveauResponsable}>Annuler</Button>
                                                <Button disabled={!nouveauResponsable.contact || !nouveauResponsable.telephone || !nouveauResponsable.email} onClick={handleClickValidNouveauResponsable}>Valider</Button>
                                            </CardActions>
                                        </React.Fragment>
                                    )}

                                </Card>
                            </Grid>


                            {fournisseur.responsables.map(responsable => {

                                return (

                                    <Grid item xs={3} key={responsable.id}>


                                        {editedResponsableId !== responsable.id && (
                                            <Card className={classes.card}>
                                                <CardContent>
                                                    <Typography gutterBottom variant="h6">
                                                        {responsable.contact}
                                                    </Typography>
                                                    <Typography gutterBottom variant="body1">
                                                        {responsable.telephone}
                                                    </Typography>
                                                    <Typography gutterBottom variant="body1">
                                                        {responsable.email}
                                                    </Typography>
                                                </CardContent>
                                                <CardActions>
                                                    <IconButton color="primary" aria-label="Supprimer" title="Supprimer le contact" onClick={() => handleClickDeleteResponsable(responsable.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                    <IconButton color="primary" aria-label="Modifier" title="Modifier le contact" onClick={() => handleClickEditResponsable(responsable.id)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </CardActions>
                                            </Card>
                                        )}

                                        {editedResponsableId === responsable.id && (
                                            <Card className={classes.card}>
                                                <CardContent>
                                                    <Typography gutterBottom variant="h6">
                                                        Modification de contact
                                                    </Typography>

                                                    <TextField
                                                        autoFocus
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Contact"
                                                        placeholder=""
                                                        helperText=""
                                                        required
                                                        value={nouveauResponsable.contact}
                                                        onChange={e => setNouveauResponsable({ ...nouveauResponsable, contact: e.target.value })}

                                                        size="small"
                                                    />
                                                    <TextField
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Téléphone"
                                                        required
                                                        value={nouveauResponsable.telephone}
                                                        onChange={e => setNouveauResponsable({ ...nouveauResponsable, telephone: e.target.value })}
                                                    />
                                                    <TextField
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Email"
                                                        required
                                                        value={nouveauResponsable.email}
                                                        onChange={e => setNouveauResponsable({ ...nouveauResponsable, email: e.target.value })}
                                                    />

                                                </CardContent>
                                                <CardActions>
                                                    <Button onClick={handleClickCancelResponsable}>Annuler</Button>
                                                    <Button disabled={!nouveauResponsable.contact || !nouveauResponsable.telephone || !nouveauResponsable.email} onClick={handleClickValidResponsable}>Valider</Button>
                                                </CardActions>
                                            </Card>
                                        )}



                                    </Grid>
                                )
                            })}


                        </Grid>

                    </AccordionDetails>
                </Accordion>


                <Accordion expanded={expandedAccordion === 'standards'} TransitionProps={{ mountOnEnter: true }} onChange={(event, isExpanded) => setExpandedAccordion(isExpanded ? 'standards' : 'NoExpandedAccordion')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Standards téléphoniques</Typography>
                    </AccordionSummary>
                    <AccordionDetails>


                        <Grid container spacing={3}>
                            <Grid item xs={3}>
                                <Card className={classes.card}>


                                    {!editionNouveauStandardTelephonique && (
                                        <CardContent className={classes.newCardContent}>
                                            <IconButton
                                                color="secondary"
                                                size="medium"
                                                title="Ajouter un standard téléphonique"
                                                onClick={handleClickNouveauStandardTelephonique}
                                            >
                                                <AddIcon />
                                            </IconButton>
                                            <Typography gutterBottom variant="body1">
                                                Ajouter un standard téléphonique
                                            </Typography>
                                        </CardContent>
                                    )}

                                    {editionNouveauStandardTelephonique && (
                                        <React.Fragment>
                                            <CardContent>
                                                <Typography gutterBottom variant="h6">
                                                    Nouveau standard téléphonique
                                                </Typography>

                                                <TextField
                                                    autoFocus
                                                    color="secondary"
                                                    fullWidth
                                                    variant="filled"
                                                    label="Numéro"
                                                    placeholder=""
                                                    helperText=""
                                                    required
                                                    value={nouveauStandardTelephonique.numero}
                                                    onChange={e => setNouveauStandardTelephonique({ ...nouveauStandardTelephonique, numero: e.target.value })}

                                                    size="small"
                                                />
                                                <TextField
                                                    color="secondary"
                                                    fullWidth
                                                    variant="filled"
                                                    label="Horaires"
                                                    required
                                                    value={nouveauStandardTelephonique.horaires}
                                                    onChange={e => setNouveauStandardTelephonique({ ...nouveauStandardTelephonique, horaires: e.target.value })}
                                                />

                                            </CardContent>
                                            <CardActions>
                                                <Button onClick={handleClickCancelNouveauStandardTelephonique}>Annuler</Button>
                                                <Button disabled={!nouveauStandardTelephonique.numero || !nouveauStandardTelephonique.horaires} onClick={handleClickValidNouveauStandardTelephonique}>Valider</Button>
                                            </CardActions>
                                        </React.Fragment>
                                    )}

                                </Card>
                            </Grid>


                            {fournisseur.standardTelephoniques.map(standardTelephonique => {

                                return (

                                    <Grid item xs={3} key={standardTelephonique.id}>


                                        {editedStandardTelephoniqueId !== standardTelephonique.id && (
                                            <Card className={classes.card}>
                                                <CardContent>
                                                    <Typography gutterBottom variant="h6">
                                                        {standardTelephonique.numero}
                                                    </Typography>
                                                    <Typography gutterBottom variant="body1">
                                                        {standardTelephonique.horaires}
                                                    </Typography>
                                                </CardContent>
                                                <CardActions>
                                                    <IconButton color="primary" aria-label="Supprimer" title="Supprimer le standard téléphonique" onClick={() => handleClickDeleteStandardTelephonique(standardTelephonique.id)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                    <IconButton color="primary" aria-label="Modifier" title="Modifier le standard téléphonique" onClick={() => handleClickEditStandardTelephonique(standardTelephonique.id)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </CardActions>
                                            </Card>
                                        )}

                                        {editedStandardTelephoniqueId === standardTelephonique.id && (
                                            <Card className={classes.card}>
                                                <CardContent>
                                                    <Typography gutterBottom variant="h6">
                                                        Modification d'un standard téléphonique
                                                    </Typography>

                                                    <TextField
                                                        autoFocus
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Numéro"
                                                        placeholder=""
                                                        helperText=""
                                                        required
                                                        value={nouveauStandardTelephonique.numero}
                                                        onChange={e => setNouveauStandardTelephonique({ ...nouveauStandardTelephonique, numero: e.target.value })}

                                                        size="small"
                                                    />
                                                    <TextField
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Horaires"
                                                        required
                                                        value={nouveauStandardTelephonique.horaires}
                                                        onChange={e => setNouveauStandardTelephonique({ ...nouveauStandardTelephonique, horaires: e.target.value })}
                                                    />

                                                </CardContent>
                                                <CardActions>
                                                    <Button onClick={handleClickCancelStandardTelephonique}>Annuler</Button>
                                                    <Button disabled={!nouveauStandardTelephonique.numero || !nouveauStandardTelephonique.horaires} onClick={handleClickValidStandardTelephonique}>Valider</Button>
                                                </CardActions>
                                            </Card>
                                        )}



                                    </Grid>
                                )
                            })}


                        </Grid>
                    </AccordionDetails>
                </Accordion>


                <Accordion expanded={expandedAccordion === 'fiches'} TransitionProps={{ mountOnEnter: true }} onChange={(event, isExpanded) => setExpandedAccordion(isExpanded ? 'fiches' : 'NoExpandedAccordion')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Fiche retour fournisseur</Typography>
                    </AccordionSummary>
                    <AccordionDetails>


                        <Grid container spacing={3}>

                            <Grid item xs={3}>
                                <DropZone id="fichesRetourDropZone" name="fichesRetourDropZone" label="Nouvelle fiche retour" buttonLabel="Ajouter une fiche" onChange={handleChangeFichier} />

                                {applyingFileErr && (<span>Une erreur est survenue, contacter le support informatique.</span>)}

                                <Button
                                    disabled={!fichierId || applyingFile}
                                    color="secondary"
                                    variant="contained"
                                    onClick={handleAppliquerFichier}
                                >
                                    Appliquer à toutes les conditions
                                    {applyingFile === true && <CircularProgress size={24} className={classes.buttonProgress} />}
                                </Button>


                            </Grid>

                            {fichiers.map(fichier => {
                                return (
                                    <React.Fragment key={fichier.id}>
                                        <Grid item xs={3}>
                                            <Card className={classes.card}>
                                                <CardActions>
                                                    <Fichier fichierId={fichier.id} />
                                                    <IconButton edge="start" className={classes.menuButton} color="inherit" aria-label="menu" onClick={handleClickMenu}>
                                                        <MenuIcon />
                                                    </IconButton>
                                                </CardActions>
                                            </Card>
                                        </Grid>


                                        <Menu
                                            id="simple-menu"
                                            anchorEl={anchorEl}
                                            keepMounted
                                            open={Boolean(anchorEl)}
                                            onClose={handleCloseMenu}
                                        >
                                            <MenuItem onClick={() => { handleSupprimerFichier(fichier.id) }}>Supprimer</MenuItem>
                                            <MenuItem onClick={() => { handleAppliquerFichier({ id: fichier.id }) }}>Appliquer à toutes les conditions</MenuItem>
                                        </Menu>

                                    </React.Fragment>
                                )
                            })}

                        </Grid>
                    </AccordionDetails>
                </Accordion>


                <Accordion expanded={expandedAccordion === 'conditions'} TransitionProps={{ mountOnEnter: true }} onChange={(event, isExpanded) => setExpandedAccordion(isExpanded ? 'conditions' : 'NoExpandedAccordion')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Conditions S.A.V. Générales</Typography>
                    </AccordionSummary>
                    <AccordionDetails classes={{
                        root: classes.conditionsAccordionDetailsRoot
                    }}>

                        <Grid container spacing={3}>

                            {!editionNouvelleCondition && (
                                <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={1}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                className={classes.button}
                                                startIcon={<AddIcon />}
                                                onClick={handleClickNouvelleConditionGenerale}
                                            >
                                                Nouvelle
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}

                            {editionNouvelleCondition && nouvelleCondition && (

                                <Grid item xs={12}>
                                    <Card>
                                        <CardHeader title="Nouvelle condition S.A.V. générale" />
                                        <CardContent>

                                            <Grid container spacing={3}>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        select
                                                        required
                                                        label="Type de garantie"
                                                        value={nouvelleCondition.garantieType !== '' ? nouvelleCondition.garantieType : ''}
                                                        onChange={e => handleChangeFieldNouvelleCondition("garantieType", e.target.value, false)}
                                                        fullWidth
                                                        variant="filled"
                                                        size="small"
                                                    >
                                                        <MenuItem key={-1} value={''}>
                                                            {'Veuillez sélectionner un type de garantie'}
                                                        </MenuItem>
                                                        {GarantieTypeNoms.map((nom, index) => (
                                                            <MenuItem key={index} value={index}>
                                                                {nom}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Grid>


                                                <Grid item xs={4}>


                                                </Grid>


                                                <Grid item xs={2}>
                                                    {nouvelleCondition.garantieType !== GarantieType.HorsGarantie && (
                                                        <TextField
                                                            id="delaiReference"
                                                            name="delaiReference"
                                                            label="Délai de référence"
                                                            placeholder={"..."}
                                                            helperText="en jours"
                                                            value={nouvelleCondition.detail.delaiReference}
                                                            onChange={e => handleChangeFieldNouvelleCondition("delaiReference", e.target.value, true)}
                                                            type="number"
                                                            inputProps={{ min: 1, max: 365 * 20 }}
                                                            InputLabelProps={{
                                                                shrink: true
                                                            }}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                            color="secondary"
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid item xs={2}>

                                                    {nouvelleCondition.garantieType !== GarantieType.HorsGarantie && (
                                                        <TextField
                                                            id="delaiExtensionGarantie"
                                                            name="delaiExtensionGarantie"
                                                            label="Délai d'extension de garantie"
                                                            placeholder={"..."}
                                                            helperText="en jours"
                                                            value={nouvelleCondition.detail.delaiExtensionGarantie ? nouvelleCondition.detail.delaiExtensionGarantie : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("delaiExtensionGarantie", e.target.value, true)}
                                                            type="number"
                                                            inputProps={{ min: nouvelleCondition.detail.delaiReference, max: 365 * 20 }}
                                                            InputLabelProps={{
                                                                shrink: true
                                                            }}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                            color="secondary"
                                                        />
                                                    )}

                                                </Grid>

                                                <Grid item xs={4}>
                                                    <TextField
                                                        select
                                                        required
                                                        label="Adresse d'envoi / de retour S.A.V."
                                                        value={nouvelleCondition.detail.adresseId}
                                                        onChange={e => handleChangeFieldNouvelleCondition("adresseId", e.target.value, true)}
                                                        fullWidth
                                                        variant="filled"
                                                        size="small"
                                                    >
                                                        {fournisseur.adresses.map(adresse => (
                                                            <MenuItem key={adresse.id} value={adresse.id}>
                                                                {adresse.libelle}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={4}>

                                                    {fournisseur.responsables.length > 0 && (

                                                        <TextField
                                                            select
                                                            label="Responsable S.A.V."
                                                            value={nouvelleCondition.detail.responsableId}
                                                            onChange={e => handleChangeFieldNouvelleCondition("responsableId", e.target.value, true)}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                        >
                                                            {fournisseur.responsables.map(responsable => (
                                                                <MenuItem key={responsable.id} value={responsable.id}>
                                                                    {responsable.contact ? responsable.contact : responsable.email ? responsable.email : responsable.telephone}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>

                                                    )}

                                                    {fournisseur.responsables.length === 0 && (

                                                        <p>Aucun responsable d'enregistré</p>

                                                    )}


                                                </Grid>
                                                <Grid item xs={4}>

                                                    {fournisseur.standardTelephoniques.length > 0 && (
                                                        <TextField
                                                            select
                                                            label="Standard téléphonique"
                                                            value={nouvelleCondition.detail.standardTelephoniqueId}
                                                            onChange={e => handleChangeFieldNouvelleCondition("standardTelephoniqueId", e.target.value, true)}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                        >
                                                            {fournisseur.standardTelephoniques.map(standardTelephonique => (
                                                                <MenuItem key={standardTelephonique.id} value={standardTelephonique.id}>
                                                                    {standardTelephonique.numero + ' / ' + standardTelephonique.horaires}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>

                                                    )}

                                                    {fournisseur.standardTelephoniques.length === 0 && (

                                                        <p>Aucun standard téléphonique d'enregistré</p>

                                                    )}
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Pièce à joindre dans le colis"
                                                        placeholder=""
                                                        helperText=""
                                                        required
                                                        value={nouvelleCondition.detail.pieceJoindreColis ? nouvelleCondition.detail.pieceJoindreColis : ''}
                                                        onChange={e => handleChangeFieldNouvelleCondition("pieceJoindreColis", e.target.value, true)}
                                                        multiline
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>

                                                    <TextField
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Périmètre d'application SAV"
                                                        placeholder=""
                                                        helperText=""
                                                        value={nouvelleCondition.detail.perimetreApplicationSAV ? nouvelleCondition.detail.perimetreApplicationSAV : ''}
                                                        onChange={e => handleChangeFieldNouvelleCondition("perimetreApplicationSAV", e.target.value, true)}
                                                        multiline
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Préconisation de facturation client"
                                                        placeholder=""
                                                        helperText=""
                                                        value={nouvelleCondition.detail.preconisationFacturationClient ? nouvelleCondition.detail.preconisationFacturationClient : ''}
                                                        onChange={e => handleChangeFieldNouvelleCondition("preconisationFacturationClient", e.target.value, true)}
                                                        multiline
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>

                                                    <TextField
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Règle prise en charge Port"
                                                        placeholder=""
                                                        helperText=""
                                                        required
                                                        value={nouvelleCondition.detail.reglePriseEnChargePort ? nouvelleCondition.detail.reglePriseEnChargePort : ''}
                                                        onChange={e => handleChangeFieldNouvelleCondition("reglePriseEnChargePort", e.target.value, true)}
                                                        multiline
                                                        size="small"
                                                    />

                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Engagement délai de resolution"
                                                        placeholder=""
                                                        helperText=""
                                                        value={nouvelleCondition.detail.engagementDelaiResolution ? nouvelleCondition.detail.engagementDelaiResolution : ''}
                                                        onChange={e => handleChangeFieldNouvelleCondition("engagementDelaiResolution", e.target.value, true)}
                                                        multiline
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <TextField
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Commentaire"
                                                        placeholder=""
                                                        helperText=""
                                                        value={nouvelleCondition.detail.commentaire ? nouvelleCondition.detail.commentaire : ''}
                                                        onChange={e => handleChangeFieldNouvelleCondition("commentaire", e.target.value, true)}
                                                        multiline
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        color="secondary"
                                                        fullWidth
                                                        variant="filled"
                                                        label="Mentions particulières"
                                                        placeholder="Mentions spécifiques pour ce fournisseur (optionnel)"
                                                        helperText="Ces mentions apparaîtront sur le formulaire de prise en charge"
                                                        value={nouvelleCondition.detail.mentionsParticulieres ? nouvelleCondition.detail.mentionsParticulieres : ''}
                                                        onChange={e => handleChangeFieldNouvelleCondition("mentionsParticulieres", e.target.value, true)}
                                                        multiline
                                                        rows={4}
                                                        size="small"
                                                    />
                                                </Grid>
                                                {fichiers.length > 0 && (

                                                    <Grid item xs={12}>

                                                        <TextField
                                                            select
                                                            label="Fiche retour fournisseur"
                                                            value={nouvelleCondition.detail.ficheRetourFournisseurId ? nouvelleCondition.detail.ficheRetourFournisseurId : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("ficheRetourFournisseurId", e.target.value, true)}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                            disabled={nouvelleCondition.detail.nouvelleFicheRetourFournisseurId > 0}
                                                            InputLabelProps={{ shrink: !!nouvelleCondition.detail.ficheRetourFournisseurId }}
                                                        >
                                                            <MenuItem key={0} value={''}>
                                                                {"Pas de fiche retour fournisseur"}
                                                            </MenuItem>
                                                            {fichiers.map(fichier => (
                                                                <MenuItem key={fichier.id} value={fichier.id}>
                                                                    {fichier.nom}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>

                                                    </Grid>

                                                )}

                                                <Grid item xs={12}>
                                                    <DropZone id="fichiersIds" name="fichiersIds" onChange={handleChangeFichierNouvelleCondition} label="Nouvelle fiche retour fournisseur" buttonLabel="Ajouter un fichier" maxNbOfFiles={1} />
                                                </Grid>


                                            </Grid>


                                        </CardContent>
                                        <CardActions>
                                            <Button onClick={handleClickCancelCondition}>Annuler</Button>

                                            {disabledNouvelleCondition.errors.length > 0 && (

                                                <Tooltip
                                                    title={disabledNouvelleCondition.errors[0].message}
                                                >
                                                    <span>
                                                        <Button disabled>Valider</Button>
                                                    </span>
                                                </Tooltip>
                                            )}

                                            {disabledNouvelleCondition.errors.length === 0 && (
                                                <Button onClick={handleClickValidCondition}>Valider</Button>
                                            )}

                                        </CardActions>
                                    </Card>
                                </Grid>
                            )}




                            {fournisseur.conditionsSAV.map(condition => {

                                let editionMode = editedConditionDetailId === condition.detail.id;

                                let adresse = condition.detail.adresseId ? fournisseur.adresses.find(adresse => adresse.id === condition.detail.adresseId) : '';
                                let responsable = condition.detail.responsableId ? fournisseur.responsables.find(responsable => responsable.id === condition.detail.responsableId) : '';
                                let standardTelephonique = condition.detail.standardTelephoniqueId ? fournisseur.standardTelephoniques.find(standardTelephonique => standardTelephonique.id === condition.detail.standardTelephoniqueId) : '';

                                // On ne peut pas supprimer une condition générale au niveau société
                                let disabledDelete = condition.perimetre === PerimetresSAV.Societe;

                                return (

                                    <React.Fragment key={condition.garantieType + condition.perimetre}>


                                        {!editionMode && (

                                            <Grid item xs={4}>
                                                <Card>

                                                    <CardHeader className={classes.conditionCardHeader}
                                                        title={GarantieTypeNoms[condition.garantieType]}

                                                        subheader={<DelaiSubHeader garantieType={condition.garantieType} delaiReference={condition.detail.delaiReference} delaiExtensionGarantie={condition.detail.delaiExtensionGarantie} />}
                                                    />
                                                    <CardContent className={classes.conditionCardContent}>


                                                        {adresse && (
                                                            <Fieldset
                                                                legend={'Adresse d\'envoi / de retour S.A.V.'}
                                                                content={adresse.libelle + '\n' + adresse.adresse}
                                                            />
                                                        )}

                                                        {responsable && (
                                                            <Fieldset
                                                                legend={'Responsable'}
                                                                content={(responsable.contact != null ? responsable.contact + '\n' : '') + (responsable.telephone != null ? responsable.telephone + '\n' : '') + (responsable.email != null ? responsable.email : '')}
                                                            />
                                                        )}


                                                        {standardTelephonique && (
                                                            <Fieldset
                                                                legend={'Standard téléphonique'}
                                                                content={standardTelephonique.numero + '\n' + standardTelephonique.horaires}
                                                            />
                                                        )}


                                                        <Fieldset
                                                            legend={'Pièce à joindre dans le colis'}
                                                            content={condition.detail.pieceJoindreColis}
                                                        />

                                                        <Fieldset
                                                            legend={'Périmètre d\'application SAV'}
                                                            content={condition.detail.perimetreApplicationSAV}
                                                        />

                                                        <Fieldset
                                                            legend={'Préconisation de facturation client'}
                                                            content={condition.detail.preconisationFacturationClient}
                                                        />

                                                        <Fieldset
                                                            legend={'Règle prise en charge Port'}
                                                            content={condition.detail.reglePriseEnChargePort}
                                                        />

                                                        <Fieldset
                                                            legend={'Engagement délai de resolution'}
                                                            content={condition.detail.engagementDelaiResolution}
                                                        />

                                                        <Fieldset
                                                            legend={'Commentaire'}
                                                            content={condition.detail.commentaire}
                                                        />

                                                        <Fieldset
                                                            legend={'Mentions particulières'}
                                                            content={condition.detail.mentionsParticulieres}
                                                        />

                                                        <Fieldset
                                                            legend={'Fiche retour fournisseur'}
                                                        >
                                                            {condition.detail.ficheRetourFournisseur && (
                                                                <Fichier fichierId={condition.detail.ficheRetourFournisseur.id} />
                                                            )}
                                                        </Fieldset>



                                                    </CardContent>
                                                    <CardActions>
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="Supprimer"
                                                            title="Supprimer"
                                                            disabled={disabledDelete}
                                                            onClick={() => handleClickDeleteCondition(condition.detail.id)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                        <IconButton color="primary" aria-label="Modifier" title="Modifier" onClick={() => handleClickEditCondition(condition.detail.id, ConditionType.Generale)}>
                                                            <EditIcon />
                                                        </IconButton>

                                                        <DuplicateButton detailId={condition.detail.id} onClick={handleClickDuplicateCondition} sourceType={ConditionType.Generale} displayArticle={isAdmin} />

                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        )}

                                        {editionMode && (

                                            <Grid item xs={4}>
                                                <Card>
                                                    <CardHeader className={classes.conditionCardHeader}
                                                        title={GarantieTypeNoms[condition.garantieType]}

                                                        subheader={<DelaiSubHeader garantieType={condition.garantieType} delaiReference={condition.detail.delaiReference} delaiExtensionGarantie={condition.detail.delaiExtensionGarantie} />}
                                                    />
                                                    <CardContent className={classes.conditionCardContent}>


                                                        {condition.garantieType !== GarantieType.HorsGarantie && (
                                                            <TextField
                                                                id="delaiReference"
                                                                name="delaiReference"
                                                                label="Délai de référence"
                                                                placeholder={"..."}
                                                                helperText="en jours"
                                                                value={nouvelleCondition.detail.delaiReference}
                                                                onChange={e => handleChangeFieldNouvelleCondition("delaiReference", e.target.value, true)}
                                                                type="number"
                                                                inputProps={{ min: 1, max: 365 * 20 }}
                                                                InputLabelProps={{
                                                                    shrink: true
                                                                }}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        )}


                                                        {condition.garantieType !== GarantieType.HorsGarantie && (
                                                            <TextField
                                                                id="delaiExtensionGarantie"
                                                                name="delaiExtensionGarantie"
                                                                label="Délai d'extension de garantie"
                                                                placeholder={"..."}
                                                                helperText="en jours"
                                                                value={nouvelleCondition.detail.delaiExtensionGarantie ? nouvelleCondition.detail.delaiExtensionGarantie : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("delaiExtensionGarantie", e.target.value, true)}
                                                                type="number"
                                                                inputProps={{ min: nouvelleCondition.detail.delaiReference, max: 365 * 20 }}
                                                                InputLabelProps={{
                                                                    shrink: true
                                                                }}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        )}


                                                        <TextField
                                                            select
                                                            required
                                                            label="Adresse d'envoi / de retour S.A.V."
                                                            value={nouvelleCondition.detail.adresseId}
                                                            onChange={e => handleChangeFieldNouvelleCondition("adresseId", e.target.value, true)}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                        >
                                                            {fournisseur.adresses.map(adresse => (
                                                                <MenuItem key={adresse.id} value={adresse.id}>
                                                                    {adresse.libelle}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>


                                                        {fournisseur.responsables.length > 0 && (

                                                            <TextField
                                                                select
                                                                label="Responsable S.A.V."
                                                                value={nouvelleCondition.detail.responsableId !== null ? nouvelleCondition.detail.responsableId : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("responsableId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.responsables.map(responsable => (
                                                                    <MenuItem key={responsable.id} value={responsable.id}>
                                                                        {responsable.contact ? responsable.contact : responsable.email ? responsable.email : responsable.telephone}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        )}

                                                        {fournisseur.responsables.length === 0 && (

                                                            <p>Aucun responsable d'enregistré</p>

                                                        )}


                                                        {fournisseur.standardTelephoniques.length > 0 && (
                                                            <TextField
                                                                select
                                                                label="Standard téléphonique"
                                                                value={nouvelleCondition.detail.standardTelephoniqueId != null ? nouvelleCondition.detail.standardTelephoniqueId : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("standardTelephoniqueId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.standardTelephoniques.map(standardTelephonique => (
                                                                    <MenuItem key={standardTelephonique.id} value={standardTelephonique.id}>
                                                                        {standardTelephonique.numero + ' / ' + standardTelephonique.horaires}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        )}

                                                        {fournisseur.standardTelephoniques.length === 0 && (

                                                            <p>Aucun standard téléphonique d'enregistré</p>

                                                        )}

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Pièce à joindre dans le colis"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.detail.pieceJoindreColis ? nouvelleCondition.detail.pieceJoindreColis : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("pieceJoindreColis", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Périmètre d'application SAV"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.perimetreApplicationSAV ? nouvelleCondition.detail.perimetreApplicationSAV : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("perimetreApplicationSAV", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Préconisation de facturation client"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.preconisationFacturationClient ? nouvelleCondition.detail.preconisationFacturationClient : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("preconisationFacturationClient", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Règle prise en charge Port"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.detail.reglePriseEnChargePort ? nouvelleCondition.detail.reglePriseEnChargePort : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("reglePriseEnChargePort", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Engagement délai de resolution"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.engagementDelaiResolution ? nouvelleCondition.detail.engagementDelaiResolution : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("engagementDelaiResolution", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Commentaire"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.commentaire ? nouvelleCondition.detail.commentaire : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("commentaire", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Mentions particulières"
                                                            placeholder="Mentions spécifiques pour ce fournisseur (optionnel)"
                                                            helperText="Ces mentions apparaîtront sur le formulaire de prise en charge"
                                                            value={nouvelleCondition.detail.mentionsParticulieres ? nouvelleCondition.detail.mentionsParticulieres : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("mentionsParticulieres", e.target.value, true)}
                                                            multiline
                                                            rows={4}
                                                            size="small"
                                                        />

                                                        {fichiers.length > 0 && (

                                                            <TextField
                                                                select
                                                                label="Nouvelle fiche retour fournisseur"
                                                                value={nouvelleCondition.detail.ficheRetourFournisseurId ? nouvelleCondition.detail.ficheRetourFournisseurId : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("ficheRetourFournisseurId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                disabled={nouvelleCondition.detail.nouvelleFicheRetourFournisseurId > 0}
                                                                InputLabelProps={{ shrink: !!nouvelleCondition.detail.ficheRetourFournisseurId }}
                                                            >
                                                                <MenuItem key={0} value={''}>
                                                                    {"Pas de fiche retour fournisseur"}
                                                                </MenuItem>
                                                                {fichiers.map(fichier => (
                                                                    <MenuItem key={fichier.id} value={fichier.id}>
                                                                        {fichier.nom}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        )}

                                                        <DropZone id="fichiersIds" name="fichiersIds" onChange={handleChangeFichierNouvelleCondition} label="Nouvelle fiche retour fournisseur" buttonLabel="Ajouter un fichier" maxNbOfFiles={1} />



                                                    </CardContent>
                                                    <CardActions>

                                                        <Button onClick={handleClickCancelCondition}>Annuler</Button>

                                                        {disabledNouvelleCondition.errors.length > 0 && (

                                                            <Tooltip
                                                                title={disabledNouvelleCondition.errors[0].message}
                                                            >
                                                                <span>
                                                                    <Button disabled={disabledNouvelleCondition.errors.length > 0} >Valider</Button>
                                                                </span>
                                                            </Tooltip>
                                                        )}

                                                        {disabledNouvelleCondition.errors.length === 0 && (
                                                            <Button onClick={handleClickValidCondition}>Valider</Button>
                                                        )}

                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        )}


                                    </React.Fragment>
                                );

                            })}

                        </Grid>


                    </AccordionDetails>
                </Accordion>


                <Accordion expanded={expandedAccordion === 'conditionsFamille'} TransitionProps={{ mountOnEnter: true }} onChange={(event, isExpanded) => setExpandedAccordion(isExpanded ? 'conditionsFamille' : 'NoExpandedAccordion')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Conditions S.A.V. pour les familles</Typography>
                    </AccordionSummary>
                    <AccordionDetails classes={{
                        root: classes.conditionsAccordionDetailsRoot
                    }}>

                        <Grid container spacing={3}>


                            {!editionNouvelleConditionFamille && (
                                <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={1}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                className={classes.button}
                                                startIcon={<AddIcon />}
                                                onClick={handleClickNouvelleConditionFamille}
                                            >
                                                Nouvelle
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}

                            {editionNouvelleConditionFamille && nouvelleCondition && (

                                <ScrollHere id={"nouvelleConditionFamille"}>
                                    <Grid item xs={12}>
                                        <Card>
                                            <CardHeader title="Nouvelle condition S.A.V. pour une famille" />
                                            <CardContent>


                                                <Grid container spacing={3}>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            select
                                                            required
                                                            label="Type de garantie"
                                                            value={nouvelleCondition.garantieType}
                                                            onChange={e => handleChangeFieldNouvelleCondition("garantieType", e.target.value, false)}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                        >
                                                            <MenuItem key={-1} value={''}>
                                                                {'Veuillez séléctionner un type de garantie'}
                                                            </MenuItem>
                                                            {GarantieTypeNoms.map((nom, index) => (
                                                                <MenuItem key={index} value={index}>
                                                                    {nom}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </Grid>

                                                    <Grid item xs={4}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Libellé"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.libelle}
                                                            onChange={e => handleChangeFieldNouvelleCondition("libelle", e.target.value, false)}

                                                            size="small"
                                                        />
                                                    </Grid>


                                                    <Grid item xs={2}>
                                                        {nouvelleCondition.garantieType !== GarantieType.HorsGarantie && (
                                                            <TextField
                                                                id="delaiReference"
                                                                name="delaiReference"
                                                                label="Délai de référence"
                                                                placeholder={"..."}
                                                                helperText="en jours"
                                                                value={nouvelleCondition.detail.delaiReference}
                                                                onChange={e => handleChangeFieldNouvelleCondition("delaiReference", e.target.value, true)}
                                                                type="number"
                                                                inputProps={{ min: 1, max: 365 * 20 }}
                                                                InputLabelProps={{
                                                                    shrink: true
                                                                }}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        )}
                                                    </Grid>
                                                    <Grid item xs={2}>

                                                        {nouvelleCondition.garantieType !== GarantieType.HorsGarantie && (
                                                            <TextField
                                                                id="delaiExtensionGarantie"
                                                                name="delaiExtensionGarantie"
                                                                label="Délai d'extension de garantie"
                                                                placeholder={"..."}
                                                                helperText="en jours"
                                                                value={nouvelleCondition.detail.delaiExtensionGarantie ? nouvelleCondition.detail.delaiExtensionGarantie : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("delaiExtensionGarantie", e.target.value, true)}
                                                                type="number"
                                                                inputProps={{ min: nouvelleCondition.detail.delaiReference, max: 365 * 20 }}
                                                                InputLabelProps={{
                                                                    shrink: true
                                                                }}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        )}

                                                    </Grid>

                                                    <Grid item xs={4}>
                                                        <TextField
                                                            select
                                                            required
                                                            label="Adresse d'envoi / de retour S.A.V."
                                                            value={nouvelleCondition.detail.adresseId}
                                                            onChange={e => handleChangeFieldNouvelleCondition("adresseId", e.target.value, true)}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                        >
                                                            {fournisseur.adresses.map(adresse => (
                                                                <MenuItem key={adresse.id} value={adresse.id}>
                                                                    {adresse.libelle}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </Grid>
                                                    <Grid item xs={4}>

                                                        {fournisseur.responsables.length > 0 && (

                                                            <TextField
                                                                select
                                                                label="Responsable S.A.V."
                                                                value={nouvelleCondition.detail.responsableId}
                                                                onChange={e => handleChangeFieldNouvelleCondition("responsableId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.responsables.map(responsable => (
                                                                    <MenuItem key={responsable.id} value={responsable.id}>
                                                                        {responsable.contact ? responsable.contact : responsable.email ? responsable.email : responsable.telephone}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        )}

                                                        {fournisseur.responsables.length === 0 && (

                                                            <p>Aucun responsable d'enregistré</p>

                                                        )}


                                                    </Grid>
                                                    <Grid item xs={4}>

                                                        {fournisseur.standardTelephoniques.length > 0 && (
                                                            <TextField
                                                                select
                                                                label="Standard téléphonique"
                                                                value={nouvelleCondition.detail.standardTelephoniqueId}
                                                                onChange={e => handleChangeFieldNouvelleCondition("standardTelephoniqueId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.standardTelephoniques.map(standardTelephonique => (
                                                                    <MenuItem key={standardTelephonique.id} value={standardTelephonique.id}>
                                                                        {standardTelephonique.numero + ' / ' + standardTelephonique.horaires}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        )}

                                                        {fournisseur.standardTelephoniques.length === 0 && (

                                                            <p>Aucun standard téléphonique d'enregistré</p>

                                                        )}
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Pièce à joindre dans le colis"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.detail.pieceJoindreColis ? nouvelleCondition.detail.pieceJoindreColis : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("pieceJoindreColis", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Périmètre d'application SAV"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.perimetreApplicationSAV ? nouvelleCondition.detail.perimetreApplicationSAV : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("perimetreApplicationSAV", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Préconisation de facturation client"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.preconisationFacturationClient ? nouvelleCondition.detail.preconisationFacturationClient : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("preconisationFacturationClient", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Règle prise en charge Port"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.detail.reglePriseEnChargePort ? nouvelleCondition.detail.reglePriseEnChargePort : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("reglePriseEnChargePort", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Engagement délai de resolution"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.engagementDelaiResolution ? nouvelleCondition.detail.engagementDelaiResolution : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("engagementDelaiResolution", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Commentaire"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.commentaire ? nouvelleCondition.detail.commentaire : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("commentaire", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Mentions particulières"
                                                            placeholder="Mentions spécifiques pour ce fournisseur (optionnel)"
                                                            helperText="Ces mentions apparaîtront sur le formulaire de prise en charge"
                                                            value={nouvelleCondition.detail.mentionsParticulieres ? nouvelleCondition.detail.mentionsParticulieres : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("mentionsParticulieres", e.target.value, true)}
                                                            multiline
                                                            rows={4}
                                                            size="small"
                                                        />
                                                    </Grid>

                                                    {fichiers.length > 0 && (

                                                        <Grid item xs={12}>

                                                            <TextField
                                                                select
                                                                label="Fiche retour fournisseur"
                                                                value={nouvelleCondition.detail.ficheRetourFournisseurId ? nouvelleCondition.detail.ficheRetourFournisseurId : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("ficheRetourFournisseurId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                disabled={nouvelleCondition.detail.nouvelleFicheRetourFournisseurId > 0}
                                                                InputLabelProps={{ shrink: !!nouvelleCondition.detail.ficheRetourFournisseurId }}
                                                            >
                                                                <MenuItem key={0} value={''}>
                                                                    {"Pas de fiche retour fournisseur"}
                                                                </MenuItem>
                                                                {fichiers.map(fichier => (
                                                                    <MenuItem key={fichier.id} value={fichier.id}>
                                                                        {fichier.nom}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        </Grid>

                                                    )}


                                                    <Grid item xs={12}>
                                                        <DropZone id="fichiersIds" name="fichiersIds" onChange={handleChangeFichierNouvelleCondition} label="Nouvelle fiche retour fournisseur" buttonLabel="Ajouter un fichier" maxNbOfFiles={1} />
                                                    </Grid>



                                                    <Grid item xs={4}>

                                                        <Fieldset
                                                            style={{ marginBottom: '8px' }}
                                                            legend={'Nouvelle liste de familles'}
                                                        >
                                                            {nouvelleCondition.nouvellesFamilles.map((famille, index) => (

                                                                <Chip
                                                                    key={index}
                                                                    label={`${famille.code} - ${famille.libelle}`}
                                                                    onDelete={() => handleDeleteFamilleNouvelleCondition(famille)}
                                                                />

                                                            ))}

                                                        </Fieldset>
                                                    </Grid>




                                                    <Grid item xs={8}>
                                                        <FamilleImportArea onImport={(famillesValides) => { handleImportFamilles(famillesValides) }} />
                                                    </Grid>



                                                </Grid>


                                            </CardContent>
                                            <CardActions>
                                                <Button onClick={handleClickCancelCondition}>Annuler</Button>

                                                {disabledNouvelleCondition.errors.length > 0 && (

                                                    <Tooltip
                                                        title={disabledNouvelleCondition.errors[0].message}
                                                    >
                                                        <span>
                                                            <Button disabled={disabledNouvelleCondition.errors.length > 0} >Valider</Button>
                                                        </span>
                                                    </Tooltip>
                                                )}

                                                {disabledNouvelleCondition.errors.length === 0 && (
                                                    <Button onClick={handleClickValidCondition}>Valider</Button>
                                                )}

                                            </CardActions>
                                        </Card>
                                    </Grid>

                                </ScrollHere>
                            )}

                            {fournisseur.conditionsSAVFamille.map(condition => {

                                let editionMode = editedConditionDetailId === condition.detail.id;

                                let adresse = condition.detail.adresseId ? fournisseur.adresses.find(adresse => adresse.id === condition.detail.adresseId) : '';
                                let responsable = condition.detail.responsableId ? fournisseur.responsables.find(responsable => responsable.id === condition.detail.responsableId) : '';
                                let standardTelephonique = condition.detail.standardTelephoniqueId ? fournisseur.standardTelephoniques.find(standardTelephonique => standardTelephonique.id === condition.detail.standardTelephoniqueId) : '';

                                return (
                                    <React.Fragment key={condition.garantieType + condition.perimetre + condition.libelle}>

                                        {!editionMode && (

                                            <Grid item xs={4}>
                                                <Card>
                                                    <CardHeader className={classes.conditionCardHeader}
                                                        title={condition.libelle}
                                                    />

                                                    <CardHeader className={classes.conditionCardHeader}
                                                        title={GarantieTypeNoms[condition.garantieType]}

                                                        subheader={<DelaiSubHeader garantieType={condition.garantieType} delaiReference={condition.detail.delaiReference} delaiExtensionGarantie={condition.detail.delaiExtensionGarantie} />}
                                                    />
                                                    <CardContent className={classes.conditionCardContent}>

                                                        {adresse && (
                                                            <Fieldset
                                                                legend={'Adresse d\'envoi / de retour S.A.V.'}
                                                                content={adresse.libelle + '\n' + adresse.adresse}
                                                            />
                                                        )}

                                                        {responsable && (
                                                            <Fieldset
                                                                legend={'Responsable'}
                                                                content={(responsable.contact ? responsable.contact + '\n' : '') + (responsable.telephone ? responsable.telephone + '\n' : '') + (responsable.email ? responsable.email : '')}
                                                            />
                                                        )}


                                                        {standardTelephonique && (
                                                            <Fieldset
                                                                legend={'Standard téléphonique'}
                                                                content={standardTelephonique.numero + '\n' + standardTelephonique.horaires}
                                                            />
                                                        )}


                                                        <Fieldset
                                                            legend={'Pièce à joindre dans le colis'}
                                                            content={condition.detail.pieceJoindreColis}
                                                        />

                                                        <Fieldset
                                                            legend={'Périmètre d\'application SAV'}
                                                            content={condition.detail.perimetreApplicationSAV}
                                                        />

                                                        <Fieldset
                                                            legend={'Préconisation de facturation client'}
                                                            content={condition.detail.preconisationFacturationClient}
                                                        />

                                                        <Fieldset
                                                            legend={'Règle prise en charge Port'}
                                                            content={condition.detail.reglePriseEnChargePort}
                                                        />

                                                        <Fieldset
                                                            legend={'Engagement délai de resolution'}
                                                            content={condition.detail.engagementDelaiResolution}
                                                        />

                                                        <Fieldset
                                                            legend={'Commentaire'}
                                                            content={condition.detail.commentaire}
                                                        />

                                                        <Fieldset
                                                            legend={'Mentions particulières'}
                                                            content={condition.detail.mentionsParticulieres}
                                                        />

                                                        <Fieldset
                                                            legend={'Fiche retour fournisseur'}
                                                        >
                                                            {condition.detail.ficheRetourFournisseur && (
                                                                <Fichier fichierId={condition.detail.ficheRetourFournisseur.id} />
                                                            )}
                                                        </Fieldset>

                                                        <Fieldset
                                                            legend={'Familles'}
                                                            content={condition.familles.reduce((accumulator, currentValue) => (accumulator ? (accumulator + "\n") : accumulator) + currentValue.code + ' - ' + currentValue.libelle, '')}
                                                        />

                                                    </CardContent>
                                                    <CardActions>
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="Supprimer"
                                                            title="Supprimer"
                                                            onClick={() => handleClickDeleteCondition(condition.detail.id)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                        <IconButton color="primary" aria-label="Modifier" title="Modifier" onClick={() => handleClickEditCondition(condition.detail.id, ConditionType.Famille)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                        <DuplicateButton detailId={condition.detail.id} onClick={handleClickDuplicateCondition} sourceType={ConditionType.Famille} displayArticle={isAdmin} />

                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        )}

                                        {editionMode && (

                                            <Grid item xs={4}>
                                                <Card>
                                                    <CardHeader className={classes.conditionCardHeader}
                                                        title={<TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Libellé"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.libelle}
                                                            onChange={e => handleChangeFieldNouvelleCondition("libelle", e.target.value, false)}

                                                            size="small"
                                                        />}
                                                    />

                                                    <CardHeader className={classes.conditionCardHeader}
                                                        title={GarantieTypeNoms[condition.garantieType]}
                                                    />
                                                    <CardContent className={classes.conditionCardContent}>



                                                        {condition.garantieType !== GarantieType.HorsGarantie && (
                                                            <TextField
                                                                id="delaiReference"
                                                                name="delaiReference"
                                                                label="Délai de référence"
                                                                placeholder={"..."}
                                                                helperText="en jours"
                                                                value={nouvelleCondition.detail.delaiReference}
                                                                onChange={e => handleChangeFieldNouvelleCondition("delaiReference", e.target.value, true)}
                                                                type="number"
                                                                inputProps={{ min: 1, max: 365 * 20 }}
                                                                InputLabelProps={{
                                                                    shrink: true
                                                                }}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        )}


                                                        {condition.garantieType !== GarantieType.HorsGarantie && (
                                                            <TextField
                                                                id="delaiExtensionGarantie"
                                                                name="delaiExtensionGarantie"
                                                                label="Délai d'extension de garantie"
                                                                placeholder={"..."}
                                                                helperText="en jours"
                                                                value={nouvelleCondition.detail.delaiExtensionGarantie ? nouvelleCondition.detail.delaiExtensionGarantie : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("delaiExtensionGarantie", e.target.value, true)}
                                                                type="number"
                                                                inputProps={{ min: nouvelleCondition.detail.delaiReference, max: 365 * 20 }}
                                                                InputLabelProps={{
                                                                    shrink: true
                                                                }}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        )}


                                                        <TextField
                                                            select
                                                            required
                                                            label="Adresse d'envoi / de retour S.A.V."
                                                            value={nouvelleCondition.detail.adresseId}
                                                            onChange={e => handleChangeFieldNouvelleCondition("adresseId", e.target.value, true)}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                        >
                                                            {fournisseur.adresses.map(adresse => (
                                                                <MenuItem key={adresse.id} value={adresse.id}>
                                                                    {adresse.libelle}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>


                                                        {fournisseur.responsables.length > 0 && (

                                                            <TextField
                                                                select
                                                                label="Responsable S.A.V."
                                                                value={nouvelleCondition.detail.responsableId !== null ? nouvelleCondition.detail.responsableId : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("responsableId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.responsables.map(responsable => (
                                                                    <MenuItem key={responsable.id} value={responsable.id}>
                                                                        {responsable.contact ? responsable.contact : responsable.email ? responsable.email : responsable.telephone}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        )}

                                                        {fournisseur.responsables.length === 0 && (

                                                            <p>Aucun responsable d'enregistré</p>

                                                        )}


                                                        {fournisseur.standardTelephoniques.length > 0 && (
                                                            <TextField
                                                                select
                                                                label="Standard téléphonique"
                                                                value={nouvelleCondition.detail.standardTelephoniqueId != null ? nouvelleCondition.detail.standardTelephoniqueId : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("standardTelephoniqueId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.standardTelephoniques.map(standardTelephonique => (
                                                                    <MenuItem key={standardTelephonique.id} value={standardTelephonique.id}>
                                                                        {standardTelephonique.numero + ' / ' + standardTelephonique.horaires}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        )}

                                                        {fournisseur.standardTelephoniques.length === 0 && (

                                                            <p>Aucun standard téléphonique d'enregistré</p>

                                                        )}

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Pièce à joindre dans le colis"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.detail.pieceJoindreColis ? nouvelleCondition.detail.pieceJoindreColis : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("pieceJoindreColis", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Périmètre d'application SAV"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.perimetreApplicationSAV ? nouvelleCondition.detail.perimetreApplicationSAV : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("perimetreApplicationSAV", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Préconisation de facturation client"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.preconisationFacturationClient ? nouvelleCondition.detail.preconisationFacturationClient : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("preconisationFacturationClient", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Règle prise en charge Port"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.detail.reglePriseEnChargePort ? nouvelleCondition.detail.reglePriseEnChargePort : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("reglePriseEnChargePort", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Engagement délai de resolution"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.engagementDelaiResolution ? nouvelleCondition.detail.engagementDelaiResolution : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("engagementDelaiResolution", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Commentaire"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.commentaire ? nouvelleCondition.detail.commentaire : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("commentaire", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Mentions particulières"
                                                            placeholder="Mentions spécifiques pour ce fournisseur (optionnel)"
                                                            helperText="Ces mentions apparaîtront sur le formulaire de prise en charge"
                                                            value={nouvelleCondition.detail.mentionsParticulieres ? nouvelleCondition.detail.mentionsParticulieres : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("mentionsParticulieres", e.target.value, true)}
                                                            multiline
                                                            rows={4}
                                                            size="small"
                                                        />

                                                        {fichiers.length > 0 && (
                                                            <TextField
                                                                select
                                                                label="Fiche retour fournisseur"
                                                                value={nouvelleCondition.detail.ficheRetourFournisseurId ? nouvelleCondition.detail.ficheRetourFournisseurId : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("ficheRetourFournisseurId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                disabled={nouvelleCondition.detail.nouvelleFicheRetourFournisseurId > 0}
                                                                InputLabelProps={{ shrink: !!nouvelleCondition.detail.ficheRetourFournisseurId }}
                                                            >
                                                                <MenuItem key={0} value={''}>
                                                                    {"Pas de fiche retour fournisseur"}
                                                                </MenuItem>
                                                                {fichiers.map(fichier => (
                                                                    <MenuItem key={fichier.id} value={fichier.id}>
                                                                        {fichier.nom}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>
                                                        )}

                                                        <DropZone id="fichiersIds" name="fichiersIds" onChange={handleChangeFichierNouvelleCondition} label="Nouvelle fiche retour fournisseur" buttonLabel="Ajouter un fichier" maxNbOfFiles={1} />




                                                        <Fieldset
                                                            legend={'Familles'}
                                                            content={condition.familles.reduce((accumulator, currentValue) => (accumulator ? (accumulator + "\n") : accumulator) + currentValue.code + ' - ' + currentValue.libelle, '')}
                                                        />



                                                        <Fieldset
                                                            style={{ marginBottom: '8px' }}
                                                            legend={'Nouvelle liste de familles'}
                                                        >
                                                            {nouvelleCondition.nouvellesFamilles.map((famille, index) => (

                                                                <Chip
                                                                    key={index}
                                                                    label={`${famille.code} - ${famille.libelle}`}
                                                                    onDelete={() => handleDeleteFamilleNouvelleCondition(famille)}
                                                                />

                                                            ))}

                                                        </Fieldset>


                                                        <FamilleImportArea onImport={(famillesValides) => { handleImportFamilles(famillesValides) }} />


                                                    </CardContent>
                                                    <CardActions>

                                                        <Button onClick={handleClickCancelCondition}>Annuler</Button>

                                                        {disabledNouvelleCondition.errors.length > 0 && (

                                                            <Tooltip
                                                                title={disabledNouvelleCondition.errors[0].message}
                                                            >
                                                                <span>
                                                                    <Button disabled={disabledNouvelleCondition.errors.length > 0} >Valider</Button>
                                                                </span>
                                                            </Tooltip>
                                                        )}

                                                        {disabledNouvelleCondition.errors.length === 0 && (
                                                            <Button onClick={handleClickValidCondition}>Valider</Button>
                                                        )}

                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        )}

                                    </React.Fragment>
                                );

                            })}

                        </Grid>


                    </AccordionDetails>
                </Accordion>


                <Accordion expanded={expandedAccordion === 'conditionsSousFamille'} TransitionProps={{ mountOnEnter: true }} onChange={(event, isExpanded) => setExpandedAccordion(isExpanded ? 'conditionsSousFamille' : 'NoExpandedAccordion')}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Conditions S.A.V. pour les sous-familles</Typography>
                    </AccordionSummary>
                    <AccordionDetails classes={{
                        root: classes.conditionsAccordionDetailsRoot
                    }}>

                        <Grid container spacing={3}>


                            {!editionNouvelleConditionSousFamille && (
                                <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={1}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                className={classes.button}
                                                startIcon={<AddIcon />}
                                                onClick={handleClickNouvelleConditionSousFamille}
                                            >
                                                Nouvelle
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}

                            {editionNouvelleConditionSousFamille && nouvelleCondition && (

                                <ScrollHere id={"nouvelleConditionSousFamille"}>
                                    <Grid item xs={12}>
                                        <Card>
                                            <CardHeader title="Nouvelle condition S.A.V. pour une sous-famille" />
                                            <CardContent>


                                                <Grid container spacing={3}>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            select
                                                            required
                                                            label="Type de garantie"
                                                            value={nouvelleCondition.garantieType}
                                                            onChange={e => handleChangeFieldNouvelleCondition("garantieType", e.target.value, false)}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                        >
                                                            <MenuItem key={-1} value={''}>
                                                                {'Veuillez séléctionner un type de garantie'}
                                                            </MenuItem>
                                                            {GarantieTypeNoms.map((nom, index) => (
                                                                <MenuItem key={index} value={index}>
                                                                    {nom}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </Grid>

                                                    <Grid item xs={4}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Libellé"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.libelle}
                                                            onChange={e => handleChangeFieldNouvelleCondition("libelle", e.target.value, false)}

                                                            size="small"
                                                        />
                                                    </Grid>


                                                    <Grid item xs={2}>
                                                        {nouvelleCondition.garantieType !== GarantieType.HorsGarantie && (
                                                            <TextField
                                                                id="delaiReference"
                                                                name="delaiReference"
                                                                label="Délai de référence"
                                                                placeholder={"..."}
                                                                helperText="en jours"
                                                                value={nouvelleCondition.detail.delaiReference}
                                                                onChange={e => handleChangeFieldNouvelleCondition("delaiReference", e.target.value, true)}
                                                                type="number"
                                                                inputProps={{ min: 1, max: 365 * 20 }}
                                                                InputLabelProps={{
                                                                    shrink: true
                                                                }}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        )}
                                                    </Grid>
                                                    <Grid item xs={2}>

                                                        {nouvelleCondition.garantieType !== GarantieType.HorsGarantie && (
                                                            <TextField
                                                                id="delaiExtensionGarantie"
                                                                name="delaiExtensionGarantie"
                                                                label="Délai d'extension de garantie"
                                                                placeholder={"..."}
                                                                helperText="en jours"
                                                                value={nouvelleCondition.detail.delaiExtensionGarantie ? nouvelleCondition.detail.delaiExtensionGarantie : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("delaiExtensionGarantie", e.target.value, true)}
                                                                type="number"
                                                                inputProps={{ min: nouvelleCondition.detail.delaiReference, max: 365 * 20 }}
                                                                InputLabelProps={{
                                                                    shrink: true
                                                                }}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        )}

                                                    </Grid>

                                                    <Grid item xs={4}>
                                                        <TextField
                                                            select
                                                            required
                                                            label="Adresse d'envoi / de retour S.A.V."
                                                            value={nouvelleCondition.detail.adresseId}
                                                            onChange={e => handleChangeFieldNouvelleCondition("adresseId", e.target.value, true)}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                        >
                                                            {fournisseur.adresses.map(adresse => (
                                                                <MenuItem key={adresse.id} value={adresse.id}>
                                                                    {adresse.libelle}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>
                                                    </Grid>
                                                    <Grid item xs={4}>

                                                        {fournisseur.responsables.length > 0 && (

                                                            <TextField
                                                                select
                                                                label="Responsable S.A.V."
                                                                value={nouvelleCondition.detail.responsableId}
                                                                onChange={e => handleChangeFieldNouvelleCondition("responsableId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.responsables.map(responsable => (
                                                                    <MenuItem key={responsable.id} value={responsable.id}>
                                                                        {responsable.contact ? responsable.contact : responsable.email ? responsable.email : responsable.telephone}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        )}

                                                        {fournisseur.responsables.length === 0 && (

                                                            <p>Aucun responsable d'enregistré</p>

                                                        )}


                                                    </Grid>
                                                    <Grid item xs={4}>

                                                        {fournisseur.standardTelephoniques.length > 0 && (
                                                            <TextField
                                                                select
                                                                label="Standard téléphonique"
                                                                value={nouvelleCondition.detail.standardTelephoniqueId}
                                                                onChange={e => handleChangeFieldNouvelleCondition("standardTelephoniqueId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.standardTelephoniques.map(standardTelephonique => (
                                                                    <MenuItem key={standardTelephonique.id} value={standardTelephonique.id}>
                                                                        {standardTelephonique.numero + ' / ' + standardTelephonique.horaires}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        )}

                                                        {fournisseur.standardTelephoniques.length === 0 && (

                                                            <p>Aucun standard téléphonique d'enregistré</p>

                                                        )}
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Pièce à joindre dans le colis"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.detail.pieceJoindreColis ? nouvelleCondition.detail.pieceJoindreColis : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("pieceJoindreColis", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Périmètre d'application SAV"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.perimetreApplicationSAV ? nouvelleCondition.detail.perimetreApplicationSAV : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("perimetreApplicationSAV", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Préconisation de facturation client"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.preconisationFacturationClient ? nouvelleCondition.detail.preconisationFacturationClient : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("preconisationFacturationClient", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Règle prise en charge Port"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.detail.reglePriseEnChargePort ? nouvelleCondition.detail.reglePriseEnChargePort : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("reglePriseEnChargePort", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Engagement délai de resolution"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.engagementDelaiResolution ? nouvelleCondition.detail.engagementDelaiResolution : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("engagementDelaiResolution", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Commentaire"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.commentaire ? nouvelleCondition.detail.commentaire : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("commentaire", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />
                                                    </Grid>

                                                    <Grid item xs={12}>
                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Mentions particulières"
                                                            placeholder="Mentions spécifiques pour ce fournisseur (optionnel)"
                                                            helperText="Ces mentions apparaîtront sur le formulaire de prise en charge"
                                                            value={nouvelleCondition.detail.mentionsParticulieres ? nouvelleCondition.detail.mentionsParticulieres : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("mentionsParticulieres", e.target.value, true)}
                                                            multiline
                                                            rows={4}
                                                            size="small"
                                                        />
                                                    </Grid>

                                                    {fichiers.length > 0 && (

                                                        <Grid item xs={12}>

                                                            <TextField
                                                                select
                                                                label="Fiche retour fournisseur"
                                                                value={nouvelleCondition.detail.ficheRetourFournisseurId ? nouvelleCondition.detail.ficheRetourFournisseurId : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("ficheRetourFournisseurId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                disabled={nouvelleCondition.detail.nouvelleFicheRetourFournisseurId > 0}
                                                                InputLabelProps={{ shrink: !!nouvelleCondition.detail.ficheRetourFournisseurId }}
                                                            >
                                                                <MenuItem key={0} value={''}>
                                                                    {"Pas de fiche retour fournisseur"}
                                                                </MenuItem>
                                                                {fichiers.map(fichier => (
                                                                    <MenuItem key={fichier.id} value={fichier.id}>
                                                                        {fichier.nom}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        </Grid>

                                                    )}


                                                    <Grid item xs={12}>
                                                        <DropZone id="fichiersIds" name="fichiersIds" onChange={handleChangeFichierNouvelleCondition} label="Nouvelle fiche retour fournisseur" buttonLabel="Ajouter un fichier" maxNbOfFiles={1} />
                                                    </Grid>



                                                    <Grid item xs={4}>

                                                        <Fieldset
                                                            style={{ marginBottom: '8px' }}
                                                            legend={'Nouvelle liste de sous-familles'}
                                                        >
                                                            {nouvelleCondition.nouvellesSousFamilles.map((sousFamille, index) => (

                                                                <Chip
                                                                    key={index}
                                                                    label={`${sousFamille.code} - ${sousFamille.libelle}`}
                                                                    onDelete={() => handleDeleteSousFamilleNouvelleCondition(sousFamille)}
                                                                />

                                                            ))}

                                                        </Fieldset>
                                                    </Grid>




                                                    <Grid item xs={8}>
                                                        <SousFamilleImportArea onImport={(sousFamillesValides) => { handleImportSousFamilles(sousFamillesValides) }} />
                                                    </Grid>



                                                </Grid>


                                            </CardContent>
                                            <CardActions>
                                                <Button onClick={handleClickCancelCondition}>Annuler</Button>

                                                {disabledNouvelleCondition.errors.length > 0 && (

                                                    <Tooltip
                                                        title={disabledNouvelleCondition.errors[0].message}
                                                    >
                                                        <span>
                                                            <Button disabled={disabledNouvelleCondition.errors.length > 0} >Valider</Button>
                                                        </span>
                                                    </Tooltip>
                                                )}

                                                {disabledNouvelleCondition.errors.length === 0 && (
                                                    <Button onClick={handleClickValidCondition}>Valider</Button>
                                                )}

                                            </CardActions>
                                        </Card>
                                    </Grid>

                                </ScrollHere>
                            )}

                            {fournisseur.conditionsSAVSousFamille.map(condition => {

                                let editionMode = editedConditionDetailId === condition.detail.id;

                                let adresse = condition.detail.adresseId ? fournisseur.adresses.find(adresse => adresse.id === condition.detail.adresseId) : '';
                                let responsable = condition.detail.responsableId ? fournisseur.responsables.find(responsable => responsable.id === condition.detail.responsableId) : '';
                                let standardTelephonique = condition.detail.standardTelephoniqueId ? fournisseur.standardTelephoniques.find(standardTelephonique => standardTelephonique.id === condition.detail.standardTelephoniqueId) : '';

                                return (
                                    <React.Fragment key={condition.garantieType + condition.perimetre + condition.libelle}>

                                        {!editionMode && (

                                            <Grid item xs={4}>
                                                <Card>
                                                    <CardHeader className={classes.conditionCardHeader}
                                                        title={condition.libelle}
                                                    />

                                                    <CardHeader className={classes.conditionCardHeader}
                                                        title={GarantieTypeNoms[condition.garantieType]}

                                                        subheader={<DelaiSubHeader garantieType={condition.garantieType} delaiReference={condition.detail.delaiReference} delaiExtensionGarantie={condition.detail.delaiExtensionGarantie} />}
                                                    />
                                                    <CardContent className={classes.conditionCardContent}>

                                                        {adresse && (
                                                            <Fieldset
                                                                legend={'Adresse d\'envoi / de retour S.A.V.'}
                                                                content={adresse.libelle + '\n' + adresse.adresse}
                                                            />
                                                        )}

                                                        {responsable && (
                                                            <Fieldset
                                                                legend={'Responsable'}
                                                                content={(responsable.contact ? responsable.contact + '\n' : '') + (responsable.telephone ? responsable.telephone + '\n' : '') + (responsable.email ? responsable.email : '')}
                                                            />
                                                        )}


                                                        {standardTelephonique && (
                                                            <Fieldset
                                                                legend={'Standard téléphonique'}
                                                                content={standardTelephonique.numero + '\n' + standardTelephonique.horaires}
                                                            />
                                                        )}


                                                        <Fieldset
                                                            legend={'Pièce à joindre dans le colis'}
                                                            content={condition.detail.pieceJoindreColis}
                                                        />

                                                        <Fieldset
                                                            legend={'Périmètre d\'application SAV'}
                                                            content={condition.detail.perimetreApplicationSAV}
                                                        />

                                                        <Fieldset
                                                            legend={'Préconisation de facturation client'}
                                                            content={condition.detail.preconisationFacturationClient}
                                                        />

                                                        <Fieldset
                                                            legend={'Règle prise en charge Port'}
                                                            content={condition.detail.reglePriseEnChargePort}
                                                        />

                                                        <Fieldset
                                                            legend={'Engagement délai de resolution'}
                                                            content={condition.detail.engagementDelaiResolution}
                                                        />

                                                        <Fieldset
                                                            legend={'Commentaire'}
                                                            content={condition.detail.commentaire}
                                                        />

                                                        <Fieldset
                                                            legend={'Mentions particulières'}
                                                            content={condition.detail.mentionsParticulieres}
                                                        />

                                                        <Fieldset
                                                            legend={'Fiche retour fournisseur'}
                                                        >
                                                            {condition.detail.ficheRetourFournisseur && (
                                                                <Fichier fichierId={condition.detail.ficheRetourFournisseur.id} />
                                                            )}
                                                        </Fieldset>

                                                        <Fieldset
                                                            legend={'Sous-Familles'}
                                                            content={condition.sousFamilles.reduce((accumulator, currentValue) => (accumulator ? (accumulator + "\n") : accumulator) + currentValue.code + ' - ' + currentValue.libelle, '')}
                                                        />

                                                    </CardContent>
                                                    <CardActions>
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="Supprimer"
                                                            title="Supprimer"
                                                            onClick={() => handleClickDeleteCondition(condition.detail.id)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                        <IconButton color="primary" aria-label="Modifier" title="Modifier" onClick={() => handleClickEditCondition(condition.detail.id, ConditionType.SousFamille)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                        <DuplicateButton detailId={condition.detail.id} onClick={handleClickDuplicateCondition} sourceType={ConditionType.SousFamille} displayArticle={isAdmin} />

                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        )}

                                        {editionMode && (

                                            <Grid item xs={4}>
                                                <Card>
                                                    <CardHeader className={classes.conditionCardHeader}
                                                        title={<TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Libellé"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.libelle}
                                                            onChange={e => handleChangeFieldNouvelleCondition("libelle", e.target.value, false)}

                                                            size="small"
                                                        />}
                                                    />

                                                    <CardHeader className={classes.conditionCardHeader}
                                                        title={GarantieTypeNoms[condition.garantieType]}
                                                    />
                                                    <CardContent className={classes.conditionCardContent}>



                                                        {condition.garantieType !== GarantieType.HorsGarantie && (
                                                            <TextField
                                                                id="delaiReference"
                                                                name="delaiReference"
                                                                label="Délai de référence"
                                                                placeholder={"..."}
                                                                helperText="en jours"
                                                                value={nouvelleCondition.detail.delaiReference}
                                                                onChange={e => handleChangeFieldNouvelleCondition("delaiReference", e.target.value, true)}
                                                                type="number"
                                                                inputProps={{ min: 1, max: 365 * 20 }}
                                                                InputLabelProps={{
                                                                    shrink: true
                                                                }}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        )}


                                                        {condition.garantieType !== GarantieType.HorsGarantie && (
                                                            <TextField
                                                                id="delaiExtensionGarantie"
                                                                name="delaiExtensionGarantie"
                                                                label="Délai d'extension de garantie"
                                                                placeholder={"..."}
                                                                helperText="en jours"
                                                                value={nouvelleCondition.detail.delaiExtensionGarantie ? nouvelleCondition.detail.delaiExtensionGarantie : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("delaiExtensionGarantie", e.target.value, true)}
                                                                type="number"
                                                                inputProps={{ min: nouvelleCondition.detail.delaiReference, max: 365 * 20 }}
                                                                InputLabelProps={{
                                                                    shrink: true
                                                                }}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                color="secondary"
                                                            />
                                                        )}


                                                        <TextField
                                                            select
                                                            required
                                                            label="Adresse d'envoi / de retour S.A.V."
                                                            value={nouvelleCondition.detail.adresseId}
                                                            onChange={e => handleChangeFieldNouvelleCondition("adresseId", e.target.value, true)}
                                                            fullWidth
                                                            variant="filled"
                                                            size="small"
                                                        >
                                                            {fournisseur.adresses.map(adresse => (
                                                                <MenuItem key={adresse.id} value={adresse.id}>
                                                                    {adresse.libelle}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>


                                                        {fournisseur.responsables.length > 0 && (

                                                            <TextField
                                                                select
                                                                label="Responsable S.A.V."
                                                                value={nouvelleCondition.detail.responsableId !== null ? nouvelleCondition.detail.responsableId : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("responsableId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.responsables.map(responsable => (
                                                                    <MenuItem key={responsable.id} value={responsable.id}>
                                                                        {responsable.contact ? responsable.contact : responsable.email ? responsable.email : responsable.telephone}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        )}

                                                        {fournisseur.responsables.length === 0 && (

                                                            <p>Aucun responsable d'enregistré</p>

                                                        )}


                                                        {fournisseur.standardTelephoniques.length > 0 && (
                                                            <TextField
                                                                select
                                                                label="Standard téléphonique"
                                                                value={nouvelleCondition.detail.standardTelephoniqueId != null ? nouvelleCondition.detail.standardTelephoniqueId : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("standardTelephoniqueId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.standardTelephoniques.map(standardTelephonique => (
                                                                    <MenuItem key={standardTelephonique.id} value={standardTelephonique.id}>
                                                                        {standardTelephonique.numero + ' / ' + standardTelephonique.horaires}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>

                                                        )}

                                                        {fournisseur.standardTelephoniques.length === 0 && (

                                                            <p>Aucun standard téléphonique d'enregistré</p>

                                                        )}

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Pièce à joindre dans le colis"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.detail.pieceJoindreColis ? nouvelleCondition.detail.pieceJoindreColis : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("pieceJoindreColis", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Périmètre d'application SAV"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.perimetreApplicationSAV ? nouvelleCondition.detail.perimetreApplicationSAV : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("perimetreApplicationSAV", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Préconisation de facturation client"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.preconisationFacturationClient ? nouvelleCondition.detail.preconisationFacturationClient : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("preconisationFacturationClient", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Règle prise en charge Port"
                                                            placeholder=""
                                                            helperText=""
                                                            required
                                                            value={nouvelleCondition.detail.reglePriseEnChargePort ? nouvelleCondition.detail.reglePriseEnChargePort : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("reglePriseEnChargePort", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Engagement délai de resolution"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.engagementDelaiResolution ? nouvelleCondition.detail.engagementDelaiResolution : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("engagementDelaiResolution", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Commentaire"
                                                            placeholder=""
                                                            helperText=""
                                                            value={nouvelleCondition.detail.commentaire ? nouvelleCondition.detail.commentaire : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("commentaire", e.target.value, true)}
                                                            multiline
                                                            size="small"
                                                        />

                                                        <TextField
                                                            color="secondary"
                                                            fullWidth
                                                            variant="filled"
                                                            label="Mentions particulières"
                                                            placeholder="Mentions spécifiques pour ce fournisseur (optionnel)"
                                                            helperText="Ces mentions apparaîtront sur le formulaire de prise en charge"
                                                            value={nouvelleCondition.detail.mentionsParticulieres ? nouvelleCondition.detail.mentionsParticulieres : ''}
                                                            onChange={e => handleChangeFieldNouvelleCondition("mentionsParticulieres", e.target.value, true)}
                                                            multiline
                                                            rows={4}
                                                            size="small"
                                                        />

                                                        {fichiers.length > 0 && (
                                                            <TextField
                                                                select
                                                                label="Fiche retour fournisseur"
                                                                value={nouvelleCondition.detail.ficheRetourFournisseurId ? nouvelleCondition.detail.ficheRetourFournisseurId : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("ficheRetourFournisseurId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                                disabled={nouvelleCondition.detail.nouvelleFicheRetourFournisseurId > 0}
                                                                InputLabelProps={{ shrink: !!nouvelleCondition.detail.ficheRetourFournisseurId }}
                                                            >
                                                                <MenuItem key={0} value={''}>
                                                                    {"Pas de fiche retour fournisseur"}
                                                                </MenuItem>
                                                                {fichiers.map(fichier => (
                                                                    <MenuItem key={fichier.id} value={fichier.id}>
                                                                        {fichier.nom}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>
                                                        )}

                                                        <DropZone id="fichiersIds" name="fichiersIds" onChange={handleChangeFichierNouvelleCondition} label="Nouvelle fiche retour fournisseur" buttonLabel="Ajouter un fichier" maxNbOfFiles={1} />




                                                        <Fieldset
                                                            legend={'Sous-Familles'}
                                                            content={condition.sousFamilles.reduce((accumulator, currentValue) => (accumulator ? (accumulator + "\n") : accumulator) + currentValue.code + ' - ' + currentValue.libelle, '')}
                                                        />



                                                        <Fieldset
                                                            style={{ marginBottom: '8px' }}
                                                            legend={'Nouvelle liste de sous-familles'}
                                                        >
                                                            {nouvelleCondition.nouvellesSousFamilles.map((sousFamille, index) => (

                                                                <Chip
                                                                    key={index}
                                                                    label={`${sousFamille.code} - ${sousFamille.libelle}`}
                                                                    onDelete={() => handleDeleteSousFamilleNouvelleCondition(sousFamille)}
                                                                />

                                                            ))}

                                                        </Fieldset>


                                                        <SousFamilleImportArea onImport={(sousFamillesValides) => { handleImportSousFamilles(sousFamillesValides) }} />


                                                    </CardContent>
                                                    <CardActions>

                                                        <Button onClick={handleClickCancelCondition}>Annuler</Button>

                                                        {disabledNouvelleCondition.errors.length > 0 && (

                                                            <Tooltip
                                                                title={disabledNouvelleCondition.errors[0].message}
                                                            >
                                                                <span>
                                                                    <Button disabled={disabledNouvelleCondition.errors.length > 0} >Valider</Button>
                                                                </span>
                                                            </Tooltip>
                                                        )}

                                                        {disabledNouvelleCondition.errors.length === 0 && (
                                                            <Button onClick={handleClickValidCondition}>Valider</Button>
                                                        )}

                                                    </CardActions>
                                                </Card>
                                            </Grid>
                                        )}

                                    </React.Fragment>
                                );

                            })}

                        </Grid>


                    </AccordionDetails>
                </Accordion>


                {isAdmin && (
                    <Accordion expanded={expandedAccordion === 'conditionsArticle'} TransitionProps={{ mountOnEnter: true }} onChange={(event, isExpanded) => setExpandedAccordion(isExpanded ? 'conditionsArticle' : 'NoExpandedAccordion')}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Conditions S.A.V. pour les articles</Typography>
                        </AccordionSummary>
                        <AccordionDetails classes={{
                            root: classes.conditionsAccordionDetailsRoot
                        }}>

                            <Grid container spacing={3}>


                                {!editionNouvelleConditionArticle && (
                                    <Grid item xs={12}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={1}>
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    className={classes.button}
                                                    startIcon={<AddIcon />}
                                                    onClick={handleClickNouvelleConditionArticle}
                                                >
                                                    Nouvelle
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}

                                {editionNouvelleConditionArticle && nouvelleCondition && (

                                    <ScrollHere id={"nouvelleConditionArticle"}>
                                        <Grid item xs={12}>
                                            <Card>
                                                <CardHeader title="Nouvelle condition S.A.V. pour un article" />
                                                <CardContent>


                                                    <Grid container spacing={3}>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                select
                                                                required
                                                                label="Type de garantie"
                                                                value={nouvelleCondition.garantieType}
                                                                onChange={e => handleChangeFieldNouvelleCondition("garantieType", e.target.value, false)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                <MenuItem key={-1} value={''}>
                                                                    {'Veuillez séléctionner un type de garantie'}
                                                                </MenuItem>
                                                                {GarantieTypeNoms.map((nom, index) => (
                                                                    <MenuItem key={index} value={index}>
                                                                        {nom}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </Grid>

                                                        <Grid item xs={4}>
                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Libellé"
                                                                placeholder=""
                                                                helperText=""
                                                                required
                                                                value={nouvelleCondition.libelle}
                                                                onChange={e => handleChangeFieldNouvelleCondition("libelle", e.target.value, false)}

                                                                size="small"
                                                            />
                                                        </Grid>


                                                        <Grid item xs={2}>
                                                            {nouvelleCondition.garantieType !== GarantieType.HorsGarantie && (
                                                                <TextField
                                                                    id="delaiReference"
                                                                    name="delaiReference"
                                                                    label="Délai de référence"
                                                                    placeholder={"..."}
                                                                    helperText="en jours"
                                                                    value={nouvelleCondition.detail.delaiReference}
                                                                    onChange={e => handleChangeFieldNouvelleCondition("delaiReference", e.target.value, true)}
                                                                    type="number"
                                                                    inputProps={{ min: 1, max: 365 * 20 }}
                                                                    InputLabelProps={{
                                                                        shrink: true
                                                                    }}
                                                                    fullWidth
                                                                    variant="filled"
                                                                    size="small"
                                                                    color="secondary"
                                                                />
                                                            )}
                                                        </Grid>
                                                        <Grid item xs={2}>

                                                            {nouvelleCondition.garantieType !== GarantieType.HorsGarantie && (
                                                                <TextField
                                                                    id="delaiExtensionGarantie"
                                                                    name="delaiExtensionGarantie"
                                                                    label="Délai d'extension de garantie"
                                                                    placeholder={"..."}
                                                                    helperText="en jours"
                                                                    value={nouvelleCondition.detail.delaiExtensionGarantie ? nouvelleCondition.detail.delaiExtensionGarantie : ''}
                                                                    onChange={e => handleChangeFieldNouvelleCondition("delaiExtensionGarantie", e.target.value, true)}
                                                                    type="number"
                                                                    inputProps={{ min: nouvelleCondition.detail.delaiReference, max: 365 * 20 }}
                                                                    InputLabelProps={{
                                                                        shrink: true
                                                                    }}
                                                                    fullWidth
                                                                    variant="filled"
                                                                    size="small"
                                                                    color="secondary"
                                                                />
                                                            )}

                                                        </Grid>

                                                        <Grid item xs={4}>
                                                            <TextField
                                                                select
                                                                required
                                                                label="Adresse d'envoi / de retour S.A.V."
                                                                value={nouvelleCondition.detail.adresseId}
                                                                onChange={e => handleChangeFieldNouvelleCondition("adresseId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.adresses.map(adresse => (
                                                                    <MenuItem key={adresse.id} value={adresse.id}>
                                                                        {adresse.libelle}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </Grid>
                                                        <Grid item xs={4}>

                                                            {fournisseur.responsables.length > 0 && (

                                                                <TextField
                                                                    select
                                                                    label="Responsable S.A.V."
                                                                    value={nouvelleCondition.detail.responsableId}
                                                                    onChange={e => handleChangeFieldNouvelleCondition("responsableId", e.target.value, true)}
                                                                    fullWidth
                                                                    variant="filled"
                                                                    size="small"
                                                                >
                                                                    {fournisseur.responsables.map(responsable => (
                                                                        <MenuItem key={responsable.id} value={responsable.id}>
                                                                            {responsable.contact ? responsable.contact : responsable.email ? responsable.email : responsable.telephone}
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>

                                                            )}

                                                            {fournisseur.responsables.length === 0 && (

                                                                <p>Aucun responsable d'enregistré</p>

                                                            )}


                                                        </Grid>
                                                        <Grid item xs={4}>

                                                            {fournisseur.standardTelephoniques.length > 0 && (
                                                                <TextField
                                                                    select
                                                                    label="Standard téléphonique"
                                                                    value={nouvelleCondition.detail.standardTelephoniqueId}
                                                                    onChange={e => handleChangeFieldNouvelleCondition("standardTelephoniqueId", e.target.value, true)}
                                                                    fullWidth
                                                                    variant="filled"
                                                                    size="small"
                                                                >
                                                                    {fournisseur.standardTelephoniques.map(standardTelephonique => (
                                                                        <MenuItem key={standardTelephonique.id} value={standardTelephonique.id}>
                                                                            {standardTelephonique.numero + ' / ' + standardTelephonique.horaires}
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>

                                                            )}

                                                            {fournisseur.standardTelephoniques.length === 0 && (

                                                                <p>Aucun standard téléphonique d'enregistré</p>

                                                            )}
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Pièce à joindre dans le colis"
                                                                placeholder=""
                                                                helperText=""
                                                                required
                                                                value={nouvelleCondition.detail.pieceJoindreColis ? nouvelleCondition.detail.pieceJoindreColis : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("pieceJoindreColis", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={4}>

                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Périmètre d'application SAV"
                                                                placeholder=""
                                                                helperText=""
                                                                value={nouvelleCondition.detail.perimetreApplicationSAV ? nouvelleCondition.detail.perimetreApplicationSAV : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("perimetreApplicationSAV", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Préconisation de facturation client"
                                                                placeholder=""
                                                                helperText=""
                                                                value={nouvelleCondition.detail.preconisationFacturationClient ? nouvelleCondition.detail.preconisationFacturationClient : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("preconisationFacturationClient", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={4}>

                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Règle prise en charge Port"
                                                                placeholder=""
                                                                helperText=""
                                                                required
                                                                value={nouvelleCondition.detail.reglePriseEnChargePort ? nouvelleCondition.detail.reglePriseEnChargePort : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("reglePriseEnChargePort", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />

                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Engagement délai de resolution"
                                                                placeholder=""
                                                                helperText=""
                                                                value={nouvelleCondition.detail.engagementDelaiResolution ? nouvelleCondition.detail.engagementDelaiResolution : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("engagementDelaiResolution", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={4}>
                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Commentaire"
                                                                placeholder=""
                                                                helperText=""
                                                                value={nouvelleCondition.detail.commentaire ? nouvelleCondition.detail.commentaire : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("commentaire", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12}>
                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Mentions particulières"
                                                                placeholder="Mentions spécifiques pour ce fournisseur (optionnel)"
                                                                helperText="Ces mentions apparaîtront sur le formulaire de prise en charge"
                                                                value={nouvelleCondition.detail.mentionsParticulieres ? nouvelleCondition.detail.mentionsParticulieres : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("mentionsParticulieres", e.target.value, true)}
                                                                multiline
                                                                rows={4}
                                                                size="small"
                                                            />
                                                        </Grid>

                                                        {fichiers.length > 0 && (

                                                            <Grid item xs={12}>

                                                                <TextField
                                                                    select
                                                                    label="Fiche retour fournisseur"
                                                                    value={nouvelleCondition.detail.ficheRetourFournisseurId ? nouvelleCondition.detail.ficheRetourFournisseurId : ''}
                                                                    onChange={e => handleChangeFieldNouvelleCondition("ficheRetourFournisseurId", e.target.value, true)}
                                                                    fullWidth
                                                                    variant="filled"
                                                                    size="small"
                                                                    disabled={nouvelleCondition.detail.nouvelleFicheRetourFournisseurId > 0}
                                                                    InputLabelProps={{ shrink: !!nouvelleCondition.detail.ficheRetourFournisseurId }}
                                                                >
                                                                    <MenuItem key={0} value={''}>
                                                                        {"Pas de fiche retour fournisseur"}
                                                                    </MenuItem>
                                                                    {fichiers.map(fichier => (
                                                                        <MenuItem key={fichier.id} value={fichier.id}>
                                                                            {fichier.nom}
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>

                                                            </Grid>

                                                        )}


                                                        <Grid item xs={12}>
                                                            <DropZone id="fichiersIds" name="fichiersIds" onChange={handleChangeFichierNouvelleCondition} label="Nouvelle fiche retour fournisseur" buttonLabel="Ajouter un fichier" maxNbOfFiles={1} />
                                                        </Grid>



                                                        <Grid item xs={4}>

                                                            <Fieldset
                                                                style={{ marginBottom: '8px' }}
                                                                legend={'Nouvelle liste d\'articles'}
                                                            >
                                                                {nouvelleCondition.nouveauxArticles.map((article, index) => (

                                                                    <Chip
                                                                        key={index}
                                                                        label={article.reference + ' - ' + article.designation}
                                                                        onDelete={() => handleDeleteReferenceNouvelleCondition(article.reference)}
                                                                    />

                                                                ))}

                                                            </Fieldset>
                                                        </Grid>




                                                        <Grid item xs={8}>
                                                            <ArticleGestionImportArea onImport={(articlesValides) => { handleImportArticles(articlesValides) }} />
                                                        </Grid>



                                                    </Grid>


                                                </CardContent>
                                                <CardActions>
                                                    <Button onClick={handleClickCancelCondition}>Annuler</Button>

                                                    {disabledNouvelleCondition.errors.length > 0 && (

                                                        <Tooltip
                                                            title={disabledNouvelleCondition.errors[0].message}
                                                        >
                                                            <span>
                                                                <Button disabled={disabledNouvelleCondition.errors.length > 0} >Valider</Button>
                                                            </span>
                                                        </Tooltip>
                                                    )}

                                                    {disabledNouvelleCondition.errors.length === 0 && (
                                                        <Button onClick={handleClickValidCondition}>Valider</Button>
                                                    )}

                                                </CardActions>
                                            </Card>
                                        </Grid>

                                    </ScrollHere>
                                )}

                                {fournisseur.conditionsSAVArticle.map(condition => {

                                    let editionMode = editedConditionDetailId === condition.detail.id;

                                    let adresse = condition.detail.adresseId ? fournisseur.adresses.find(adresse => adresse.id === condition.detail.adresseId) : '';
                                    let responsable = condition.detail.responsableId ? fournisseur.responsables.find(responsable => responsable.id === condition.detail.responsableId) : '';
                                    let standardTelephonique = condition.detail.standardTelephoniqueId ? fournisseur.standardTelephoniques.find(standardTelephonique => standardTelephonique.id === condition.detail.standardTelephoniqueId) : '';

                                    return (
                                        <React.Fragment key={condition.garantieType + condition.perimetre + condition.libelle}>

                                            {!editionMode && (

                                                <Grid item xs={4}>
                                                    <Card>
                                                        <CardHeader className={classes.conditionCardHeader}
                                                            title={condition.libelle}
                                                        />

                                                        <CardHeader className={classes.conditionCardHeader}
                                                            title={GarantieTypeNoms[condition.garantieType]}

                                                            subheader={<DelaiSubHeader garantieType={condition.garantieType} delaiReference={condition.detail.delaiReference} delaiExtensionGarantie={condition.detail.delaiExtensionGarantie} />}
                                                        />
                                                        <CardContent className={classes.conditionCardContent}>

                                                            {adresse && (
                                                                <Fieldset
                                                                    legend={'Adresse d\'envoi / de retour S.A.V.'}
                                                                    content={adresse.libelle + '\n' + adresse.adresse}
                                                                />
                                                            )}

                                                            {responsable && (
                                                                <Fieldset
                                                                    legend={'Responsable'}
                                                                    content={(responsable.contact ? responsable.contact + '\n' : '') + (responsable.telephone ? responsable.telephone + '\n' : '') + (responsable.email ? responsable.email : '')}
                                                                />
                                                            )}


                                                            {standardTelephonique && (
                                                                <Fieldset
                                                                    legend={'Standard téléphonique'}
                                                                    content={standardTelephonique.numero + '\n' + standardTelephonique.horaires}
                                                                />
                                                            )}


                                                            <Fieldset
                                                                legend={'Pièce à joindre dans le colis'}
                                                                content={condition.detail.pieceJoindreColis}
                                                            />

                                                            <Fieldset
                                                                legend={'Périmètre d\'application SAV'}
                                                                content={condition.detail.perimetreApplicationSAV}
                                                            />

                                                            <Fieldset
                                                                legend={'Préconisation de facturation client'}
                                                                content={condition.detail.preconisationFacturationClient}
                                                            />

                                                            <Fieldset
                                                                legend={'Règle prise en charge Port'}
                                                                content={condition.detail.reglePriseEnChargePort}
                                                            />

                                                            <Fieldset
                                                                legend={'Engagement délai de resolution'}
                                                                content={condition.detail.engagementDelaiResolution}
                                                            />

                                                            <Fieldset
                                                                legend={'Commentaire'}
                                                                content={condition.detail.commentaire}
                                                            />

                                                            <Fieldset
                                                                legend={'Mentions particulières'}
                                                                content={condition.detail.mentionsParticulieres}
                                                            />

                                                            <Fieldset
                                                                legend={'Fiche retour fournisseur'}
                                                            >
                                                                {condition.detail.ficheRetourFournisseur && (
                                                                    <Fichier fichierId={condition.detail.ficheRetourFournisseur.id} />
                                                                )}
                                                            </Fieldset>

                                                            <Fieldset
                                                                legend={'Articles'}
                                                                content={condition.articles.reduce((accumulator, currentValue) => (accumulator ? (accumulator + "\n") : accumulator) + currentValue.reference + ' - ' + currentValue.designation, '')}
                                                            />

                                                        </CardContent>
                                                        <CardActions>
                                                            <IconButton
                                                                color="primary"
                                                                aria-label="Supprimer"
                                                                title="Supprimer"
                                                                onClick={() => handleClickDeleteCondition(condition.detail.id)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                            <IconButton color="primary" aria-label="Modifier" title="Modifier" onClick={() => handleClickEditCondition(condition.detail.id, ConditionType.Article)}>
                                                                <EditIcon />
                                                            </IconButton>
                                                            <DuplicateButton detailId={condition.detail.id} onClick={handleClickDuplicateCondition} sourceType={ConditionType.Article} displayArticle={isAdmin} />
                                                        </CardActions>
                                                    </Card>
                                                </Grid>
                                            )}

                                            {editionMode && (

                                                <Grid item xs={4}>
                                                    <Card>
                                                        <CardHeader className={classes.conditionCardHeader}
                                                            title={<TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Libellé"
                                                                placeholder=""
                                                                helperText=""
                                                                required
                                                                value={nouvelleCondition.libelle}
                                                                onChange={e => handleChangeFieldNouvelleCondition("libelle", e.target.value, false)}

                                                                size="small"
                                                            />}
                                                        />

                                                        <CardHeader className={classes.conditionCardHeader}
                                                            title={GarantieTypeNoms[condition.garantieType]}
                                                        />
                                                        <CardContent className={classes.conditionCardContent}>



                                                            {condition.garantieType !== GarantieType.HorsGarantie && (
                                                                <TextField
                                                                    id="delaiReference"
                                                                    name="delaiReference"
                                                                    label="Délai de référence"
                                                                    placeholder={"..."}
                                                                    helperText="en jours"
                                                                    value={nouvelleCondition.detail.delaiReference}
                                                                    onChange={e => handleChangeFieldNouvelleCondition("delaiReference", e.target.value, true)}
                                                                    type="number"
                                                                    inputProps={{ min: 1, max: 365 * 20 }}
                                                                    InputLabelProps={{
                                                                        shrink: true
                                                                    }}
                                                                    fullWidth
                                                                    variant="filled"
                                                                    size="small"
                                                                    color="secondary"
                                                                />
                                                            )}


                                                            {condition.garantieType !== GarantieType.HorsGarantie && (
                                                                <TextField
                                                                    id="delaiExtensionGarantie"
                                                                    name="delaiExtensionGarantie"
                                                                    label="Délai d'extension de garantie"
                                                                    placeholder={"..."}
                                                                    helperText="en jours"
                                                                    value={nouvelleCondition.detail.delaiExtensionGarantie ? nouvelleCondition.detail.delaiExtensionGarantie : ''}
                                                                    onChange={e => handleChangeFieldNouvelleCondition("delaiExtensionGarantie", e.target.value, true)}
                                                                    type="number"
                                                                    inputProps={{ min: nouvelleCondition.detail.delaiReference, max: 365 * 20 }}
                                                                    InputLabelProps={{
                                                                        shrink: true
                                                                    }}
                                                                    fullWidth
                                                                    variant="filled"
                                                                    size="small"
                                                                    color="secondary"
                                                                />
                                                            )}


                                                            <TextField
                                                                select
                                                                required
                                                                label="Adresse d'envoi / de retour S.A.V."
                                                                value={nouvelleCondition.detail.adresseId}
                                                                onChange={e => handleChangeFieldNouvelleCondition("adresseId", e.target.value, true)}
                                                                fullWidth
                                                                variant="filled"
                                                                size="small"
                                                            >
                                                                {fournisseur.adresses.map(adresse => (
                                                                    <MenuItem key={adresse.id} value={adresse.id}>
                                                                        {adresse.libelle}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>


                                                            {fournisseur.responsables.length > 0 && (

                                                                <TextField
                                                                    select
                                                                    label="Responsable S.A.V."
                                                                    value={nouvelleCondition.detail.responsableId !== null ? nouvelleCondition.detail.responsableId : ''}
                                                                    onChange={e => handleChangeFieldNouvelleCondition("responsableId", e.target.value, true)}
                                                                    fullWidth
                                                                    variant="filled"
                                                                    size="small"
                                                                >
                                                                    {fournisseur.responsables.map(responsable => (
                                                                        <MenuItem key={responsable.id} value={responsable.id}>
                                                                            {responsable.contact ? responsable.contact : responsable.email ? responsable.email : responsable.telephone}
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>

                                                            )}

                                                            {fournisseur.responsables.length === 0 && (

                                                                <p>Aucun responsable d'enregistré</p>

                                                            )}


                                                            {fournisseur.standardTelephoniques.length > 0 && (
                                                                <TextField
                                                                    select
                                                                    label="Standard téléphonique"
                                                                    value={nouvelleCondition.detail.standardTelephoniqueId != null ? nouvelleCondition.detail.standardTelephoniqueId : ''}
                                                                    onChange={e => handleChangeFieldNouvelleCondition("standardTelephoniqueId", e.target.value, true)}
                                                                    fullWidth
                                                                    variant="filled"
                                                                    size="small"
                                                                >
                                                                    {fournisseur.standardTelephoniques.map(standardTelephonique => (
                                                                        <MenuItem key={standardTelephonique.id} value={standardTelephonique.id}>
                                                                            {standardTelephonique.numero + ' / ' + standardTelephonique.horaires}
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>

                                                            )}

                                                            {fournisseur.standardTelephoniques.length === 0 && (

                                                                <p>Aucun standard téléphonique d'enregistré</p>

                                                            )}

                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Pièce à joindre dans le colis"
                                                                placeholder=""
                                                                helperText=""
                                                                required
                                                                value={nouvelleCondition.detail.pieceJoindreColis ? nouvelleCondition.detail.pieceJoindreColis : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("pieceJoindreColis", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />

                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Périmètre d'application SAV"
                                                                placeholder=""
                                                                helperText=""
                                                                value={nouvelleCondition.detail.perimetreApplicationSAV ? nouvelleCondition.detail.perimetreApplicationSAV : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("perimetreApplicationSAV", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />

                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Préconisation de facturation client"
                                                                placeholder=""
                                                                helperText=""
                                                                value={nouvelleCondition.detail.preconisationFacturationClient ? nouvelleCondition.detail.preconisationFacturationClient : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("preconisationFacturationClient", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />

                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Règle prise en charge Port"
                                                                placeholder=""
                                                                helperText=""
                                                                required
                                                                value={nouvelleCondition.detail.reglePriseEnChargePort ? nouvelleCondition.detail.reglePriseEnChargePort : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("reglePriseEnChargePort", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />

                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Engagement délai de resolution"
                                                                placeholder=""
                                                                helperText=""
                                                                value={nouvelleCondition.detail.engagementDelaiResolution ? nouvelleCondition.detail.engagementDelaiResolution : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("engagementDelaiResolution", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />

                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Commentaire"
                                                                placeholder=""
                                                                helperText=""
                                                                value={nouvelleCondition.detail.commentaire ? nouvelleCondition.detail.commentaire : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("commentaire", e.target.value, true)}
                                                                multiline
                                                                size="small"
                                                            />

                                                            <TextField
                                                                color="secondary"
                                                                fullWidth
                                                                variant="filled"
                                                                label="Mentions particulières"
                                                                placeholder="Mentions spécifiques pour ce fournisseur (optionnel)"
                                                                helperText="Ces mentions apparaîtront sur le formulaire de prise en charge"
                                                                value={nouvelleCondition.detail.mentionsParticulieres ? nouvelleCondition.detail.mentionsParticulieres : ''}
                                                                onChange={e => handleChangeFieldNouvelleCondition("mentionsParticulieres", e.target.value, true)}
                                                                multiline
                                                                rows={4}
                                                                size="small"
                                                            />

                                                            {fichiers.length > 0 && (
                                                                <TextField
                                                                    select
                                                                    label="Fiche retour fournisseur"
                                                                    value={nouvelleCondition.detail.ficheRetourFournisseurId ? nouvelleCondition.detail.ficheRetourFournisseurId : ''}
                                                                    onChange={e => handleChangeFieldNouvelleCondition("ficheRetourFournisseurId", e.target.value, true)}
                                                                    fullWidth
                                                                    variant="filled"
                                                                    size="small"
                                                                    disabled={nouvelleCondition.detail.nouvelleFicheRetourFournisseurId > 0}
                                                                    InputLabelProps={{ shrink: !!nouvelleCondition.detail.ficheRetourFournisseurId }}
                                                                >
                                                                    <MenuItem key={0} value={''}>
                                                                        {"Pas de fiche retour fournisseur"}
                                                                    </MenuItem>
                                                                    {fichiers.map(fichier => (
                                                                        <MenuItem key={fichier.id} value={fichier.id}>
                                                                            {fichier.nom}
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>
                                                            )}

                                                            <DropZone id="fichiersIds" name="fichiersIds" onChange={handleChangeFichierNouvelleCondition} label="Nouvelle fiche retour fournisseur" buttonLabel="Ajouter un fichier" maxNbOfFiles={1} />




                                                            <Fieldset
                                                                legend={'Articles'}
                                                                content={condition.articles.reduce((accumulator, currentValue) => (accumulator ? (accumulator + "\n") : accumulator) + currentValue.reference + ' - ' + currentValue.designation, '')}
                                                            />



                                                            <Fieldset
                                                                style={{ marginBottom: '8px' }}
                                                                legend={'Nouvelle liste d\'articles'}
                                                            >
                                                                {nouvelleCondition.nouveauxArticles.map((article, index) => (

                                                                    <Chip
                                                                        key={index}
                                                                        label={article.reference + ' - ' + article.designation}
                                                                        onDelete={() => handleDeleteReferenceNouvelleCondition(article.reference)}
                                                                    />

                                                                ))}

                                                            </Fieldset>


                                                            <ArticleGestionImportArea onImport={(articlesValides) => { handleImportArticles(articlesValides) }} />


                                                        </CardContent>
                                                        <CardActions>

                                                            <Button onClick={handleClickCancelCondition}>Annuler</Button>

                                                            {disabledNouvelleCondition.errors.length > 0 && (

                                                                <Tooltip
                                                                    title={disabledNouvelleCondition.errors[0].message}
                                                                >
                                                                    <span>
                                                                        <Button disabled={disabledNouvelleCondition.errors.length > 0} >Valider</Button>
                                                                    </span>
                                                                </Tooltip>
                                                            )}

                                                            {disabledNouvelleCondition.errors.length === 0 && (
                                                                <Button onClick={handleClickValidCondition}>Valider</Button>
                                                            )}

                                                        </CardActions>
                                                    </Card>
                                                </Grid>
                                            )}

                                        </React.Fragment>
                                    );

                                })}

                            </Grid>


                        </AccordionDetails>
                    </Accordion>
                )}

            </ContainerWithLoader>
        </React.Fragment>
    );
}

const mapDispatchToProps = dispatch => ({
    updateDropZone: dropZone => dispatch(updateDropZone(dropZone))
});

export default connect(null, mapDispatchToProps)(withStyles(styles)(FournisseurGestionConditionsSAVEditeur));
